// 📞 보이스톡 통화 도크 — WebRTC 1:1 음성 통화 + 화면 공유. ChatPage 에 1회 마운트.
// 시작: 'tpt:call-start' {username, name?} (사용자 카드/통화 로그 카드) / 수신: 'tpt:call-incoming'
//   {callId, rid, fromUsername, fromName} (ChatPage 가 [TPT_CALL] invite 마커 실시간 수신 시 발사).
// 미디어: 마이크 ON·비디오 OFF 로 시작(음성 전용). video transceiver 를 미리 협상해 두고
//   화면 공유는 replaceTrack 만으로 켜고/끄기(재협상 불필요). 통화 중 제어는 RTCDataChannel('tpt-ctl').
// 시그널링: offer/answer 는 사이드카 tpt_calls 문서 — 벨울림 동안만 1s 폴링(참여자 2명 한정, 단기).
// ⚠️ getUserMedia/getDisplayMedia 는 secure context 전용 — HTTP 웹/웹뷰에선 안내 후 중단(insecureCtx).

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { avatarUrl, rcOpenDm, rcSendMessage, rcUpdateMessage, rcGwWhois, formatNameRole, type GwWhois } from '../../api/rcClient';
import {
	buildCallMarker, callAccept, callDecline, callEnd, callIceConfig, callReport, callSignal, callStart, callState,
} from '../../api/call';
import { tptAlert, humanizeError } from '../../stores/modalStore';
import { diagLog } from '../../desktop/diag';
import { isTauri } from '../../desktop';
import { openPipWindow, pipSupported } from '../../utils/pipWindow';
import { Icon } from '../icons';
import ScreenSourcePicker, { type CaptureSource } from '../common/ScreenSourcePicker';
import { getNotifPrefs } from '../../api/prefs';
import { cornerClass } from '../../utils/cornerPos';
import { useDragOffset } from '../../hooks/useDragOffset';
import { isIosApp, isAndroidApp } from '../../utils/platform';
import { nativeCallAvailable, isMicFailReason, NativeCallSession } from '../../api/nativeCall';
import { waitIce } from './iceGather';

type Phase = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'active';

// ⏱ 발신 단계 상한 (2026-09-09) — 어느 promise 가 영영 pending 이어도 사용자에게 어느 단계인지 알린다.
const MIC_TIMEOUT_S = 12;
const STAGE_TIMEOUT_S = 15;
const TIMEOUT_ERR = 'TptCallTimeout';
function withTimeout<T>(p: Promise<T>, sec: number, msg: string): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const id = window.setTimeout(() => { const err = new Error(msg); err.name = TIMEOUT_ERR; reject(err); }, sec * 1000);
		p.then((v) => { window.clearTimeout(id); resolve(v); }, (e) => { window.clearTimeout(id); reject(e); });
	});
}

interface Session {
	callId: string;
	rid: string;
	peerUsername: string;
	peerName: string;
	role: 'caller' | 'callee';
	mode: 'voice' | 'screen';   // screen=화면만 공유(음성 없음) — 벨울림 UI/미디어 구성 분기
	msgId?: string;        // caller 전용 — invite 마커 메시지(종료 시 최종 상태로 수정)
	startedAt?: number;    // 연결 시각(ms) — 타이머/통화시간
}

export interface CallDockProps {
	authToken: string;
	userId: string;
	username: string;
	displayName?: string;
}

// 🧊 waitIce(ICE 후보 수집 대기)는 ./iceGather 로 분리 — 재시작 직후 stale complete 함정(2026-10-07) 설명도 거기

// 벨소리 — 수신(incoming)·발신 대기(ringback) 모두 회사 지정 음원 voicetalk.wav 루프 재생
//   (2026-08-12 수신 지정 → 2026-08-22 사용자 "거는 쪽에도 동일한 연결음" 으로 발신도 통일,
//   public/sounds → dist 번들). 발신 대기음은 연결까지 계속 울리므로 볼륨만 낮춘다.
//   재생 실패(autoplay 차단·에셋 유실) 시 기존 WebAudio 합성 폴백. 반환=정지 함수.
function startTone(kind: 'incoming' | 'ringback'): () => void {
	try {
		const a = new Audio('/sounds/voicetalk.wav');
		a.loop = true;
		if (kind === 'ringback') a.volume = 0.6;
		const p = a.play();
		if (p && typeof p.catch === 'function') {
			let fallbackStop: (() => void) | null = null;
			let stopped = false;
			p.catch(() => { if (!stopped) fallbackStop = startSynthTone(kind); });
			return () => { stopped = true; try { a.pause(); a.src = ''; } catch { /* */ } fallbackStop?.(); };
		}
		return () => { try { a.pause(); a.src = ''; } catch { /* */ } };
	} catch { return startSynthTone(kind); }
}
// (구) WebAudio 합성 벨 — 폴백 + 발신 대기음.
//   구형 PSTN 톤(425Hz 뚜-, 2026-08-07 사용자 "너무 구형") → 배음+감쇠 엔벨로프의 부드러운 벨 음색으로 재설계:
//   incoming=밝은 3음 상행 차임(띠리링), ringback=따뜻한 2음 모티프(둠-딩, 현대 VoIP 풍).
//   autoplay 정책으로 suspended 면 무음(시각 UI 는 항상 동작) — resume 시도만 하고 실패 무시.
function startSynthTone(kind: 'incoming' | 'ringback'): () => void {
	try {
		const Ctor = (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
		if (!Ctor) return () => { /* no-op */ };
		const ctx = new Ctor();
		void ctx.resume().catch(() => undefined);
		// 🎵 공용 체인 (2026-08-10 사용자 "기계음 말고 현대적으로") — 이전엔 배음을 그대로 destination 에
		//   찔러 넣어 날카롭고 전자음 같았다. lowpass 로 고역을 눌러 부드럽게 + 슬랩백 딜레이로 공간감을
		//   준다(에셋 없이 리버브 흉내). 음역대도 한 옥타브 낮춰 귀에 편하게.
		const lp = ctx.createBiquadFilter();
		lp.type = 'lowpass';
		lp.frequency.value = 2400;
		lp.Q.value = 0.7;
		const master = ctx.createGain();
		master.gain.value = 1;
		lp.connect(master);
		master.connect(ctx.destination);
		const delay = ctx.createDelay(1.0);
		delay.delayTime.value = 0.17;
		const fb = ctx.createGain();
		fb.gain.value = 0.26;              // 짧은 반복 — 길면 지저분해진다
		const wet = ctx.createGain();
		wet.gain.value = 0.30;
		master.connect(delay);
		delay.connect(fb);
		fb.connect(delay);
		delay.connect(wet);
		wet.connect(ctx.destination);
		// 벨 음색 — 기본음 + 옥타브/5도 배음을 아주 여리게. 살짝 디튠한 유니즌으로 두께를 준다.
		const bell = (freq: number, atSec: number, durSec: number, vol: number) => {
			const t0 = ctx.currentTime + atSec;
			([[1, 1, 0], [1, 0.5, 1.6], [2, 0.16, 0], [3, 0.05, 0]] as const).forEach(([mult, g, detune]) => {
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = 'sine';
				osc.frequency.value = freq * mult;
				osc.detune.value = detune;
				gain.gain.setValueAtTime(0, t0);
				gain.gain.linearRampToValueAtTime(vol * g, t0 + 0.045);   // 어택을 늘려 '땡' → '둥'
				gain.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec);
				osc.connect(gain).connect(lp);
				osc.start(t0); osc.stop(t0 + durSec + 0.05);
			});
		};
		const cycle = kind === 'incoming'
			// 수신 — D5→F#5→A5 (D 메이저) 완만한 상행. 이전(G5~E6)보다 낮아 덜 쨍하다.
			? () => {
				bell(587, 0, 0.9, 0.13);
				bell(740, 0.18, 1.0, 0.13);
				bell(880, 0.36, 1.6, 0.14);
			}
			// 발신 대기 — F4→C5 낮고 따뜻한 2음. 여운을 길게 끌어 '연결 중' 느낌.
			: () => {
				bell(349, 0, 1.1, 0.10);
				bell(523, 0.34, 1.8, 0.08);
			};
		cycle();
		const iv = window.setInterval(cycle, kind === 'incoming' ? 2600 : 3400);
		return () => { window.clearInterval(iv); void ctx.close().catch(() => undefined); };
	} catch { return () => { /* no-op */ }; }
}

// 🖥 Rust 네이티브 화면 캡처 (2026-08-07) — 데스크톱 WebView2 의 getDisplayMedia 가 이 환경에서
//   프레임을 전혀 만들지 못함(공유자 본인 미리보기까지 검음, WGC 비활성 플래그도 무효 — 실증).
//   Rust(screen_grab, xcap)로 주 모니터를 ~4fps JPEG 폴링 → canvas.captureStream 트랙으로 변환.
//   WebRTC 파이프라인(협상·replaceTrack)은 기존 그대로. v1 은 주 모니터 전체 화면 고정(창 선택 없음).
// WebCodecs 트랙 생성기 — lib.dom 타입 미포함 대비 최소 선언 (Chromium 94+, WebView2 포함)
declare class MediaStreamTrackGenerator extends MediaStreamTrack {
	constructor(init: { kind: 'video' | 'audio' });
	writable: WritableStream;
}

let _lastNativeCapErr = '';   // 실패 사유 — 사용자 알림에 그대로 노출 (조용한 검은 화면 방지)

// 네이티브 캡처 대상 — Rust screen_sources() 응답 (모니터/창)
export type { CaptureSource };

// 🖥 화면 송신 부스트 (2026-08-07 #3 — 작게 시작해 아주 천천히 커지는 초기 램프업 해소, 사내 LAN 전제)
//   contentHint=detail: 인코더가 프레임률 대신 해상도 유지(텍스트 선명) / maxBitrate·maintain-resolution 강제.
async function boostVideoSender(sender: RTCRtpSender, track: MediaStreamTrack): Promise<void> {
	try { track.contentHint = 'detail'; } catch { /* */ }
	try {
		const p = sender.getParameters();
		if (!p.encodings || p.encodings.length === 0) p.encodings = [{}];
		p.encodings[0].maxBitrate = 10_000_000;
		p.degradationPreference = 'maintain-resolution';
		await sender.setParameters(p);
	} catch { /* 일부 스택 미지원 — 무시 */ }
}

// 원격 SDP 의 비디오 코덱에 x-google 시작/최대 비트레이트 힌트 + b=AS 삽입 — 초기 저해상도 램프업 단축.
//   munge 실패/거부 대비: 호출측이 원본 SDP 로 1회 재시도.
function boostVideoSdp(sdp: string): string {
	try {
		const lines = sdp.split('\r\n');
		// 기존 fmtp 가 있는 pt 는 건드리지 않음(중복 fmtp 는 파서가 거부할 수 있음)
		const hasFmtp = new Set<string>();
		let inVideo = false;
		for (const l of lines) {
			if (l.startsWith('m=')) inVideo = l.startsWith('m=video');
			if (!inVideo) continue;
			const f = l.match(/^a=fmtp:(\d+) /);
			if (f) hasFmtp.add(f[1]);
		}
		const out: string[] = [];
		inVideo = false;
		let basAdded = false;
		for (const l of lines) {
			if (l.startsWith('m=')) { inVideo = l.startsWith('m=video'); basAdded = false; }
			out.push(l);
			if (inVideo && !basAdded && l.startsWith('c=')) { out.push('b=AS:10000'); basAdded = true; }
			if (inVideo) {
				const m = l.match(/^a=rtpmap:(\d+) (?:VP8|VP9|H264)\//);
				if (m && !hasFmtp.has(m[1])) {
					out.push(`a=fmtp:${m[1]} x-google-start-bitrate=4000;x-google-min-bitrate=1000;x-google-max-bitrate=10000`);
					hasFmtp.add(m[1]);
				}
			}
		}
		return out.join('\r\n');
	} catch { return sdp; }
}

// 🎞 WebCodecs 직결 경로 (2026-08-07, v0.2.300) — canvas.captureStream 은 이 WebView2 환경에서
//   자동/requestFrame/뷰포트부착 모두 프레임 0 실측(v0.2.299 src=0). MediaStreamTrackGenerator 에
//   JPEG→VideoFrame 을 직접 기록 — 컴포지팅·가시성 완전 무관, 결정적 프레임 공급.
//   125ms 간격(≈8fps, busy 가드로 캡처 소요시간에 자동 적응) — 250ms(4fps)가 "엄청 느림" 피드백 (0.2.302).
async function startWebCodecsScreenTrack(
	invoke: <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>,
	target?: CaptureSource,
): Promise<{ track: MediaStreamTrack; stop: () => void }> {
	const gen = new MediaStreamTrackGenerator({ kind: 'video' });
	const writer = (gen.writable as WritableStream<VideoFrame>).getWriter();
	// 🚀 상주 스트림 (0.2.303 속도 2차) — Rust 스레드가 ~10fps 로 계속 캡처·인코딩, 여기선 최신
	//   프레임만 즉시 수신(캡처와 전송·디코딩 파이프라이닝). 변화 없으면 빈 응답(디코딩 생략).
	await invoke('screen_stream_start', target ? { kind: target.kind, id: target.id } : {});
	let lastSeq = 0;
	const pullOnce = async (): Promise<boolean> => {
		const buf = await invoke<ArrayBuffer>('screen_stream_frame', { lastSeq });
		if (!buf || buf.byteLength <= 8) return false;   // 새 프레임 없음
		const dv = new DataView(buf);
		lastSeq = Number(dv.getBigUint64(0, true));
		const bmp = await createImageBitmap(new Blob([buf.slice(8)], { type: 'image/jpeg' }));
		const frame = new VideoFrame(bmp, { timestamp: Math.round(performance.now() * 1000) });
		bmp.close();
		try { await writer.write(frame); }   // write 가 frame 소유권을 가져감 — 성공 시 close 불필요
		catch (e) { try { frame.close(); } catch { /* */ } throw e; }
		return true;
	};
	// 첫 프레임 — 스레드가 첫 인코딩을 마칠 때까지 최대 4s 대기, 실패 시 throw(호출측이 canvas 폴백/알림)
	const t0 = Date.now();
	while (!(await pullOnce())) {
		if (Date.now() - t0 > 4000) {
			try { await invoke('screen_stream_stop'); } catch { /* */ }
			throw new Error('첫 프레임 시간 초과');
		}
		await new Promise((r) => setTimeout(r, 100));
	}
	let busy = false;
	const iv = window.setInterval(() => {
		if (busy) return;
		busy = true;
		pullOnce().catch(() => undefined).finally(() => { busy = false; });
	}, 100);
	return {
		track: gen,
		stop: () => {
			window.clearInterval(iv);
			void invoke('screen_stream_stop').catch(() => undefined);
			try { void writer.close().catch(() => undefined); } catch { /* */ }
			try { gen.stop(); } catch { /* */ }
		},
	};
}

async function startNativeScreenTrack(target?: CaptureSource): Promise<{ track: MediaStreamTrack; stop: () => void } | null> {
	let canvas: HTMLCanvasElement | null = null;
	try {
		const { invoke } = await import('@tauri-apps/api/core');
		// 1순위: WebCodecs 직결 (Chromium 94+) — canvas 캡처의 프레임 미공급 문제 원천 회피
		if (typeof MediaStreamTrackGenerator !== 'undefined' && typeof VideoFrame !== 'undefined') {
			try {
				const wc = await startWebCodecsScreenTrack(invoke, target);
				diagLog('native capture', `webcodecs track OK (${target ? `${target.kind}#${target.id}` : 'primary'})`);
				return wc;
			} catch (e) {
				diagLog('webcodecs capture fail — canvas 폴백', String((e as Error).message || e));
			}
		}
		canvas = document.createElement('canvas');
		// 🔴 canvas 는 뷰포트 '안'에 두어야 컴포지팅됨 (화면 밖/미부착은 자동 캡처 0프레임) — 우하단 2px·투명.
		//   requestFrame 수동 푸시와 이중 안전장치.
		canvas.style.cssText = 'position:fixed;right:0;bottom:0;width:2px;height:2px;opacity:0.01;pointer-events:none;';
		document.body.appendChild(canvas);
		const c2d = canvas.getContext('2d');
		if (!c2d) throw new Error('canvas 2d context 실패');
		const cv = canvas;
		let pushFrame: () => void = () => { /* 트랙 생성 전 noop */ };
		const grabArgs = target ? { kind: target.kind, id: target.id } : undefined;
		const drawOnce = async () => {
			const buf = await invoke<ArrayBuffer>('screen_grab', grabArgs);
			const bmp = await createImageBitmap(new Blob([buf], { type: 'image/jpeg' }));
			if (cv.width !== bmp.width || cv.height !== bmp.height) { cv.width = bmp.width; cv.height = bmp.height; }
			c2d.drawImage(bmp, 0, 0);
			bmp.close();
			pushFrame();
		};
		await drawOnce();   // 첫 프레임 확보 — 실패 시 throw → 호출측이 사유 알림
		// 🔴 captureStream(fps) 자동 캡처는 canvas 가 화면 밖이면 컴포지팅이 생략돼 프레임 0 (0.2.297 실증
		//   — "네이티브 캡처" 라벨까지 뜨는데 미리보기·전송 모두 검음). frameRate 0 + requestFrame() 수동
		//   푸시는 가시성과 무관하게 프레임을 강제 공급하는 표준 API.
		const stream = cv.captureStream(0);
		const track = stream.getVideoTracks()[0];
		if (!track) throw new Error('captureStream 트랙 없음');
		const rqTrack = track as MediaStreamTrack & { requestFrame?: () => void };
		const rqStream = stream as MediaStream & { requestFrame?: () => void };
		pushFrame = () => { try { if (rqTrack.requestFrame) rqTrack.requestFrame(); else rqStream.requestFrame?.(); } catch { /* */ } };
		pushFrame();   // 이미 그려진 첫 프레임 즉시 푸시
		let busy = false;   // 캡처가 간격보다 느릴 때 겹침 방지
		const iv = window.setInterval(() => {
			if (busy) return;
			busy = true;
			drawOnce().catch(() => undefined).finally(() => { busy = false; });
		}, 250);
		return {
			track,
			stop: () => { window.clearInterval(iv); try { track.stop(); } catch { /* */ } try { cv.remove(); } catch { /* */ } },
		};
	} catch (e) {
		_lastNativeCapErr = String((e as Error).message || e);
		diagLog('native capture fail', _lastNativeCapErr);
		try { canvas?.remove(); } catch { /* */ }
		return null;
	}
}

export default function CallDock({ authToken, userId, username, displayName }: CallDockProps) {
	const { t, i18n } = useTranslation();
	const [phase, setPhase] = useState<Phase>('idle');
	const [session, setSession] = useState<Session | null>(null);
	const [muted, setMuted] = useState(false);
	const [noMic, setNoMic] = useState(false);   // 🎤 장치 없음 — 수신 전용 통화 (음소거 버튼 비활성)
	const [peerMuted, setPeerMuted] = useState(false);
	const [peerNoMic, setPeerNoMic] = useState(false);   // 상대 마이크 미감지 — "왜 안 들리지" 진단용 표시
	const [sharing, setSharing] = useState(false);            // 내 화면 공유 중
	// 🔊 통화 소리 출력 (APK code 31+) — 기본 수화기(2026-08-07 사용자: "기본은 수화기로") ↔ 스피커폰 토글.
	const [speakerOn, setSpeakerOn] = useState(false);
	// 👤 상대 조직 정보 (2026-08-07 사용자 — 사진/이름에 더해 회사·부서·직책) — whois 캐시 조회, 준회원 뷰어는 403 → 이름만
	const [peerWhois, setPeerWhois] = useState<GwWhois | null>(null);
	// 🖥 사용 중 캡처 방식 — native(Rust)/browser(getDisplayMedia). 도크에 표기해 현장 진단 가능하게.
	const [capMode, setCapMode] = useState<'native' | 'browser' | null>(null);
	// 🖥 화면 송신 정밀 수치 (src=캡처 프레임/enc=인코딩 프레임/muted) — 전송 0 경고 시 도크에 병기(스크린샷 진단용)
	const [shareDbg, setShareDbg] = useState('');
	// 🖥 공유 대상 선택 모달 (2026-08-07 사용자 — "창/모니터 선택 옵션") — 네이티브 캡처는 자체 픽커가 없어 직접 제공
	const [sharePicker, setSharePicker] = useState<{ sources: CaptureSource[]; proceed: (s: CaptureSource) => void; cancel: () => void } | null>(null);
	// 픽커 썸네일 (kind#id → objectURL) — 열릴 때 비동기 로드, 닫힐 때 revoke
	const [remoteScreenOn, setRemoteScreenOn] = useState(false);
	const [remoteVideo, setRemoteVideo] = useState<MediaStream | null>(null);
	const [screenMin, setScreenMin] = useState(false);         // 수신 화면 최소화(도크 썸네일)
	// 🪟 상대 화면 별도 창 (Document PiP, 2026-08-18) — null 이면 이 창의 오버레이로 본다.
	const [pipWin, setPipWin] = useState<Window | null>(null);
	const [tick, setTick] = useState(0);                       // 타이머 리렌더
	// 📊 미디어 흐름 진단 (2026-08-07 #2) — 방향별(음성 송/수신·화면 송/수신) 바이트 증가 여부. null=측정 전
	const [diag, setDiag] = useState<{ aIn: boolean; aOut: boolean; vIn: boolean; vOut: boolean } | null>(null);

	const phaseRef = useRef<Phase>('idle');
	phaseRef.current = phase;
	const sessionRef = useRef<Session | null>(null);
	sessionRef.current = session;
	// 📱 안드로이드 알림 [받기] — 네이티브가 넘긴 callId. incoming 세션이 뜨기 전에 도착할 수 있어 보관 후 소비.
	const pendingAcceptRef = useRef<string | null>(null);
	const pcRef = useRef<RTCPeerConnection | null>(null);
	// 🔁 통화 중 재연결(ICE 재시작, 2026-10-06 형 "보이스톡 20~30초마다 끊김 — 운전 중 LTE") — 발신자: 기다리는 회차 / 수신자: 마지막으로 처리한 회차
	const reSeqRef = useRef(0);
	const restartsRef = useRef(0);          // 통화당 재시작 횟수(상한 5) — 망이 영영 안 돌아오면 유예 끝에 종료
	const restartingRef = useRef(false);
	const iceRestartRef = useRef<() => void>(() => undefined);   // buildPc 핸들러에서 부른다(선언 순서 무관)
	// 🍎 iOS 네이티브 통화 미디어 세션 (2026-09-17) — voice 모드에서 셸이 nativeCall* 브릿지를 주입하면 RTCPeerConnection 대신 이것.
	//   웹은 여전히 통화의 주인(상태기·시그널링·마커) — 미디어(마이크·PC·오디오 세션)만 셸. 계약은 src/api/nativeCall.ts.
	const nativeRef = useRef<NativeCallSession | null>(null);
	const nativeAnswerAppliedRef = useRef(false);   // 발신 폴링에서 answer 적용 1회 가드 (pc 경로의 currentRemoteDescription 대응)
	const dcRef = useRef<RTCDataChannel | null>(null);
	const localStreamRef = useRef<MediaStream | null>(null);
	const screenTrackRef = useRef<MediaStreamTrack | null>(null);
	const sharingRef = useRef(false);
	const noMicRef = useRef(false);   // dc onopen 시 상대에게 'nomic' 통지용 (state 는 클로저 stale)
	const nativeCapStopRef = useRef<(() => void) | null>(null);   // 네이티브 캡처 폴링 정지 (공유 종료/정리 시)
	const pollRef = useRef<number | null>(null);
	const timeoutRef = useRef<number | null>(null);
	const disconnectGraceRef = useRef<number | null>(null);
	const stopToneRef = useRef<(() => void) | null>(null);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const endingRef = useRef(false);                           // cleanup 재진입 가드
	// 📊 종료 사유 꼬리표 (2026-10-13 끊김 보완) — endCall 직전에 경로별로 박아 두면 endCall 이 서버 통화 기록(call/report)에 싣는다
	const endDetailRef = useRef('');

	// 🪟 상대 화면을 Document PiP 창으로 분리 — 창을 닫으면(pagehide) 자동으로 이 창 오버레이로 되돌아온다.
	const openScreenWindow = useCallback(async () => {
		const w = await openPipWindow(960, 600);
		if (!w) { void tptAlert(t('call.screenPopoutUnsupported', { defaultValue: '이 환경에서는 별도 창으로 볼 수 없습니다.' })); return; }
		w.addEventListener('pagehide', () => setPipWin(null));
		setPipWin(w);
	}, [t]);

	// 상대가 공유를 끄면 별도 창도 닫는다 — 스트림이 사라진 빈 창이 떠 있으면 "멈춘 것처럼" 보인다.
	useEffect(() => {
		if (!pipWin || remoteScreenOn) return;
		try { pipWin.close(); } catch { /* */ }
		setPipWin(null);
	}, [pipWin, remoteScreenOn]);

	const stopRing = useCallback(() => { stopToneRef.current?.(); stopToneRef.current = null; }, []);
	const clearPoll = useCallback(() => {
		if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
		if (timeoutRef.current) { window.clearTimeout(timeoutRef.current); timeoutRef.current = null; }
		if (disconnectGraceRef.current) { window.clearTimeout(disconnectGraceRef.current); disconnectGraceRef.current = null; }
	}, []);

	// 전체 해제 — 어떤 경로로 끝나든 반드시 여기로 (트랙/피어/폴링/벨 정리)
	const cleanup = useCallback(() => {
		stopRing(); clearPoll();
		sharingRef.current = false;
		try { nativeCapStopRef.current?.(); } catch { /* */ }
		nativeCapStopRef.current = null;
		try { screenTrackRef.current?.stop(); } catch { /* */ }
		screenTrackRef.current = null;
		try { localStreamRef.current?.getTracks().forEach((tr) => tr.stop()); } catch { /* */ }
		localStreamRef.current = null;
		try { dcRef.current?.close(); } catch { /* */ }
		dcRef.current = null;
		try { pcRef.current?.close(); } catch { /* */ }
		pcRef.current = null;
		try { nativeRef.current?.end(); } catch { /* */ }   // 🍎 마이크·오디오 세션 해제 — 어떤 경로로 끝나든 반드시 (안 부르면 셸에 남는다)
		nativeRef.current = null;
		nativeAnswerAppliedRef.current = false;
		reSeqRef.current = 0; restartsRef.current = 0; restartingRef.current = false;
		if (audioRef.current) { try { audioRef.current.srcObject = null; } catch { /* */ } }
		// 🪟 별도 화면 창은 통화가 끝나면 함께 닫는다 (스트림이 끊긴 빈 창이 남지 않게)
		setPipWin((w) => { try { w?.close(); } catch { /* */ } return null; });
		noMicRef.current = false;
		setPhase('idle'); setSession(null); setMuted(false); setNoMic(false); setPeerMuted(false); setPeerNoMic(false);
		setSharing(false); setRemoteScreenOn(false); setRemoteVideo(null); setScreenMin(false); setSpeakerOn(false); setCapMode(null); setPeerWhois(null); setShareDbg('');
		endingRef.current = false;
	}, [stopRing, clearPoll]);

	// 발신자만 — invite 마커 메시지를 최종 상태(ended/missed/declined)로 수정해 통화 로그 카드 확정
	const finalizeMarker = useCallback(async (state: 'ended' | 'missed' | 'declined', dur?: number) => {
		const s = sessionRef.current;
		if (!s || s.role !== 'caller' || !s.msgId) return;
		try {
			await rcUpdateMessage(authToken, userId, s.rid, s.msgId, buildCallMarker({
				kind: 'call', callId: s.callId, state, from: username, to: s.peerUsername,
				fromName: displayName || username, mode: s.mode, ...(dur !== undefined ? { dur: Math.round(dur) } : {}),
			}));
		} catch { /* 마커 수정 실패해도 통화 종료엔 지장 없음 */ }
	}, [authToken, userId, username, displayName]);

	// 통화 종료(공통) — reason: local(내가 종료)/remote(상대 종료·연결 끊김)/declined/missed
	const endCall = useCallback((reason: 'local' | 'remote' | 'declined' | 'missed') => {
		if (endingRef.current) return;
		endingRef.current = true;
		const s = sessionRef.current;
		const wasActive = phaseRef.current === 'active';
		const dur = wasActive && s?.startedAt ? (Date.now() - s.startedAt) / 1000 : 0;
		try {
			if (reason === 'local') {
				if (dcRef.current?.readyState === 'open') dcRef.current.send(JSON.stringify({ t: 'bye' }));
				else nativeRef.current?.dcSend({ t: 'bye' });   // 🍎 네이티브 경로 — 셸이 dc 를 지원하면 상대에게 즉시 종료 통지
			}
		} catch { /* */ }
		if (s) {
			// 서버 상태 종결(멱등) — declined 는 이미 서버에 기록됨
			if (reason !== 'declined') void callEnd(authToken, userId, s.callId).catch(() => undefined);
			if (wasActive) void finalizeMarker('ended', dur);
			else if (reason === 'declined') void finalizeMarker('declined');
			else if (s.role === 'caller') void finalizeMarker('missed');
			// 📊 통화 기록 (2026-10-13 끊김 보완) — 연결까지 간 통화만(벨만 울린 건 마커로 충분). 누가·왜·몇 초·재연결 몇 번·어느 기기
			if (wasActive || phaseRef.current === 'connecting') {
				callReport(authToken, userId, {
					callId: s.callId, role: s.role, mode: s.mode, reason, detail: endDetailRef.current, dur: Math.round(dur),
					restarts: restartsRef.current, platform: isIosApp() ? 'ios' : isAndroidApp() ? 'android' : isTauri() ? 'desktop' : 'web',
					appVer: __APP_VERSION__, phase: phaseRef.current,
				});
			}
		}
		endDetailRef.current = '';
		cleanup();
	}, [authToken, userId, finalizeMarker, cleanup]);

	// 🕐 통화 활성 전환 (2026-08-22 사용자 — "받기를 누르면 시간이 바로 가는데 실제 연결은 3~4초 뒤") —
	//   음성 통화는 pc 'connected'(ICE/DTLS)만으로는 소리가 아직 안 흐를 수 있어, 원격 오디오 트랙에
	//   RTP 가 실제 도달(onunmute)했을 때 타이머(startedAt)를 시작한다. 그 전까지는 '연결 중…' 유지.
	//   화면 전용·무마이크 상대(오디오가 영영 안 옴) 대비 — connected 후 1.5s 폴백으로 활성 전환.
	const activateIfConnecting = useCallback(() => {
		if (phaseRef.current !== 'connecting') return;
		stopRing(); clearPoll();
		setSession((cur) => (cur ? { ...cur, startedAt: cur.startedAt || Date.now() } : cur));
		setPhase('active');
		phaseRef.current = 'active';   // ref 는 렌더 후 갱신 — 직후 폴백 타이머·unmute 가 중복 진입하지 않게 수동 동기화
		const s = sessionRef.current;
		if (s?.mode === 'screen' && s.role === 'callee') { setRemoteScreenOn(true); setScreenMin(false); }
	}, [stopRing, clearPoll]);

	// RTCPeerConnection 생성 + 공통 핸들러 (ontrack/datachannel/connectionstate)
	const buildPc = useCallback(async (): Promise<RTCPeerConnection> => {
		const iceServers = await callIceConfig();
		// 🔴 TURN 강제 경유 (2026-08-12 "한쪽만 들림/무음" 수정) — 서버 앞 방화벽이 외부 단말들을 같은
		//   공인 IP(14.51.179.254)로 SNAT 해서, WebRTC 가 서로를 '같은 네트워크'로 오판하고 직접(srflx/host)
		//   연결을 밀어붙이다 한 방향만 뚫리는 문제. relay 만 쓰게 강제하면 양쪽이 각자 TURN 에 allocation 을
		//   만들어 대칭 중계 → 방향성 무음 제거. TURN 자격이 있을 때만 강제(없으면 기존 동작 유지 — 폴백).
		//   ⚠️ 대가: 사내 단말끼리도 서버를 경유(대역폭↑)하지만 음성은 통화당 ~50kbps 라 감당. 검증 후
		//   direct 를 부분 허용할지는 relay 포트 도달성 확정 뒤 판단.
		const forceRelay = iceServers.some((s) => {
			const u = s.urls; const arr = Array.isArray(u) ? u : [u];
			return arr.some((x) => typeof x === 'string' && x.startsWith('turn'));
		});
		const pc = new RTCPeerConnection(forceRelay ? { iceServers, iceTransportPolicy: 'relay' } : { iceServers });
		pc.ontrack = (e) => {
			if (e.track.kind === 'audio') {
				const stream = e.streams[0] || new MediaStream([e.track]);
				if (audioRef.current) {
					audioRef.current.srcObject = stream;
					void audioRef.current.play().catch(() => undefined);
				}
				// 🕐 실제 음성(RTP) 도달 → 그때 통화 활성(타이머 시작). 이미 흐르는 중이면 즉시.
				e.track.onunmute = () => activateIfConnecting();
				if (!e.track.muted) activateIfConnecting();
				return;
			}
			// video = 상대 화면 공유 트랙 — dc 'screen' 신호가 주지만 unmute 로도 백업 감지.
			// ⚠️ onmute 로 뷰어를 닫지 않는다 (2026-08-07 #1) — 정지 화면은 RTP 가 멈춰 수신 트랙 mute 가
			//   흔한데, 이때 오버레이가 언마운트되면 되살릴 신호가 없어 "화면이 안 보임"이 됨.
			//   의도적 중지는 dc {t:'screen',on:false}(통화 중 공유)·세션 종료(화면 전용)가 처리.
			const vs = e.streams[0] || new MediaStream([e.track]);
			setRemoteVideo(vs);
			e.track.onunmute = () => setRemoteScreenOn(true);
		};
		pc.onconnectionstatechange = () => {
			const st = pc.connectionState;
			if (st === 'connected') {
				if (disconnectGraceRef.current) { window.clearTimeout(disconnectGraceRef.current); disconnectGraceRef.current = null; }
				if (phaseRef.current === 'connecting') {
					const s = sessionRef.current;
					if (s?.mode === 'voice') {
						// 🕐 음성은 실제 소리 도달(ontrack onunmute)에 활성 — 1.5s 내 안 오면(상대 무마이크 등) 폴백
						window.setTimeout(() => activateIfConnecting(), 1500);
					} else {
						activateIfConnecting();   // 화면 전용 — 연결 즉시 (수신측 화면 보기 오픈 포함)
					}
				}
				return;
			}
			if (st === 'closed') { if (phaseRef.current === 'active' || phaseRef.current === 'connecting') { endDetailRef.current = 'pc-closed'; endCall('remote'); } return; }
			// 🔁 통화 중(active) 끊김·실패 = 재연결 시도 (2026-10-06) — 이동 중(기지국 전환·VPN)엔 수초씩 끊긴다. 종전엔 8s 뒤 바로 종료(재시도 없음)라
			//   20~30초마다 통화가 끊겼다. 발신자가 ICE 재시작(reoffer)을 올리고, 25s 유예 안에 복구 안 되면 종료. 연결 중(connecting) 실패는 종전대로 즉시 종료.
			if (st === 'failed' && phaseRef.current !== 'active') { if (phaseRef.current === 'connecting') { endDetailRef.current = 'connect-failed'; endCall('remote'); } return; }
			if ((st === 'disconnected' || st === 'failed') && (phaseRef.current === 'active' || phaseRef.current === 'connecting')) {
				if (phaseRef.current === 'active') iceRestartRef.current();
				if (!disconnectGraceRef.current) {
					disconnectGraceRef.current = window.setTimeout(() => {
						disconnectGraceRef.current = null;
						if (pc.connectionState !== 'connected' && (phaseRef.current === 'active' || phaseRef.current === 'connecting')) { endDetailRef.current = phaseRef.current === 'active' ? 'ice-timeout' : 'connect-timeout'; endCall('remote'); }
					}, phaseRef.current === 'active' ? 25_000 : 8000);
				}
			}
		};
		pc.ondatachannel = (e) => { dcRef.current = e.channel; wireDc(e.channel); };
		return pc;
	}, [endCall, activateIfConnecting]);

	// 통화 중 제어 메시지('tpt-ctl') 해석 — 웹 dc onmessage 와 🍎 네이티브 dc 이벤트가 공용으로 쓴다
	const handleCtl = useCallback((raw: string) => {
		try {
			const m = JSON.parse(raw) as { t?: string; on?: boolean };
			if (m.t === 'bye') { endDetailRef.current = 'peer-bye'; endCall('remote'); return; }
			if (m.t === 'mute') setPeerMuted(!!m.on);
			if (m.t === 'nomic') setPeerNoMic(true);
			// 🍎 네이티브 경로는 화면 수신이 없다(음성 전용 — 상대 offer 의 video 는 셸이 inactive) → 화면 보기 UI 를 열지 않는다
			if (m.t === 'screen' && !nativeRef.current) { setRemoteScreenOn(!!m.on); if (m.on) setScreenMin(false); }
		} catch { /* 형식 외 무시 */ }
	}, [endCall]);

	const wireDc = useCallback((dc: RTCDataChannel) => {
		// 연결 직후 내 상태 통지 — 마이크 미감지(수신 전용)면 상대가 "왜 안 들리지" 하지 않게 표시.
		// 화면 공유 중(화면 전용 세션 포함)이면 'screen on' 명시 신호 — 수신측이 mode 추론에만 의존하지 않게 (2026-08-07 #1)
		dc.onopen = () => {
			try { if (noMicRef.current) dc.send(JSON.stringify({ t: 'nomic' })); } catch { /* */ }
			try { if (sharingRef.current) dc.send(JSON.stringify({ t: 'screen', on: true })); } catch { /* */ }
		};
		dc.onmessage = (e) => handleCtl(String(e.data));
	}, [handleCtl]);

	// 🍎 네이티브 통화 미디어 세션 생성 — pc 의 onconnectionstatechange/ondatachannel 에 대응하는 핸들러를 건다.
	//   ontrack 이 없으므로 'connected' 즉시 활성(타이머 시작). disconnected 유예는 pc 경로와 같게 — 통화 중 25s · 연결 중 8s (2026-10-13).
	const buildNative = useCallback((callId: string): NativeCallSession => {
		const ns: NativeCallSession = new NativeCallSession(callId, {
			onState: (st) => {
				if (nativeRef.current !== ns) return;   // 이미 정리된 세션의 늦은 이벤트
				if (st === 'connected') {
					if (disconnectGraceRef.current) { window.clearTimeout(disconnectGraceRef.current); disconnectGraceRef.current = null; }
					activateIfConnecting();
					return;
				}
				if (st === 'failed' || st === 'ended') {
					// 벨울림(outgoing) 중 셸 실패도 즉시 정리 — 안 하면 60s 부재중 타임아웃까지 매달린다
					if (phaseRef.current === 'active' || phaseRef.current === 'connecting' || phaseRef.current === 'outgoing') { endDetailRef.current = `native-${st}`; endCall('remote'); }
					return;
				}
				// 🍎 통화 중(active) 유예 8s → 25s (2026-10-13 형 "ios도 할 거 있으면") — 엘리베이터·약한 신호처럼 잠깐 흔들리는 끊김은
				//   네이티브 WebRTC 가 ICE 재시작 없이 스스로 회복하는 경우가 많은데, 8초는 그 전에 통화를 끊었다. 웹 경로(pc)와 같은 25s 로.
				//   iOS 네이티브는 ICE 재시작을 못 하므로(셸 계약에 없음) 망 전환(Wi-Fi↔LTE)은 여전히 이 유예 뒤 종료 — 연결 중(connecting) 실패는 종전대로 8s.
				if (st === 'disconnected' && (phaseRef.current === 'active' || phaseRef.current === 'connecting') && !disconnectGraceRef.current) {
					disconnectGraceRef.current = window.setTimeout(() => {
						disconnectGraceRef.current = null;
						if (nativeRef.current === ns && (phaseRef.current === 'active' || phaseRef.current === 'connecting')) { endDetailRef.current = 'native-ice-timeout'; endCall('remote'); }
					}, phaseRef.current === 'active' ? 25_000 : 8000);
				}
			},
			onDc: (json) => { if (nativeRef.current === ns) handleCtl(json); },
		});
		return ns;
	}, [activateIfConnecting, endCall, handleCtl]);

	const dcSend = (obj: Record<string, unknown>) => {
		try {
			if (dcRef.current?.readyState === 'open') dcRef.current.send(JSON.stringify(obj));
			else nativeRef.current?.dcSend(obj);   // 🍎 네이티브 경로 (셸 미지원이면 조용히 생략)
		} catch { /* */ }
	};

	// 🍎 네이티브 실패 사유(계약 ③) → 안내 문구 — mic 계열은 권한 안내, 나머지는 원문
	const nativeFailMsg = useCallback((e: unknown): string => {
		const err = e as Error | undefined;
		if (err?.name !== 'NativeCallFailed') return humanizeError(e);
		const reason = err.message;
		if (isMicFailReason(reason)) {
			return t('call.micTimeout', { defaultValue: '마이크 응답이 없습니다({{sec}}초). 앱의 마이크 권한(설정 > 앱 > TPTalk > 권한)을 확인하고 다시 시도해 주세요.', sec: MIC_TIMEOUT_S });
		}
		// 'timeout' — 셸은 마이크 권한을 **처음 묻는 순간**에도 기다리지 않고 timeout 으로 올린다(맥 (4)): 권한을 허용한 뒤 다시 걸면 된다
		if (reason === 'timeout') return t('call.stageTimeout', { defaultValue: '{{stage}} 응답 없음({{sec}}초)', stage: 'mic', sec: STAGE_TIMEOUT_S });
		if (reason === 'ice' || reason === 'sdp') return t('call.connectFail', { defaultValue: '통화 연결에 실패했습니다. 네트워크를 확인해 주세요.' });
		return humanizeError(e);
	}, [t]);

	// 마이크 획득 — 음성 전용(video:false). secure context 아니면 mediaDevices 자체가 없음.
	// 마이크 획득 — 🎤 없어도 통화는 진행(수신 전용, 2026-08-06 사용자: 화면 공유에 마이크 강제 X).
	//   ok=false 는 insecure context(HTTP 웹) 하드 차단만. 장치 없음/점유 등은 stream=null 로 계속.
	// ⏱ 단계 타임아웃 (2026-09-09 사용자 "안드로이드 앱 사용자 카드에서 보이스톡 누르면 카드만 닫히고 반응 없음") —
	//   getUserMedia·DM 열기·통화 요청 등 어느 하나가 영영 안 돌아오면 phase 는 idle 인데 화면엔 아무 표시가 없어
	//   "안 됨" 으로만 보였다. 단계마다 상한을 두고 어느 단계에서 멈췄는지 알림에 병기해 원인을 좁힌다.
	const getMic = useCallback(async (): Promise<{ stream: MediaStream | null; ok: boolean }> => {
		if (!navigator.mediaDevices?.getUserMedia) {
			void tptAlert(t('call.insecureCtx', { defaultValue: '이 환경에서는 통화(마이크)를 사용할 수 없습니다. 데스크톱 앱 또는 HTTPS 접속에서 이용해 주세요.' }));
			return { stream: null, ok: false };
		}
		try {
			// 안드로이드 웹뷰는 권한 요청(onPermissionRequest)이 응답을 못 받으면 promise 가 영영 pending — 상한 후 안내·중단.
			const req = navigator.mediaDevices.getUserMedia({ audio: true, video: false });
			try {
				return { stream: await withTimeout(req, MIC_TIMEOUT_S, t('call.stageTimeout', { defaultValue: '{{stage}} 응답 없음({{sec}}초)', stage: 'mic', sec: MIC_TIMEOUT_S })), ok: true };
			} catch (e) {
				// 상한 뒤 늦게 열린 마이크는 바로 닫는다 — 통화가 없는데 마이크가 켜진 채 남지 않게
				if ((e as Error)?.name === TIMEOUT_ERR) req.then((s) => s.getTracks().forEach((tr) => tr.stop()), () => undefined);
				throw e;
			}
		} catch (e) {
			if ((e as Error)?.name === TIMEOUT_ERR) {
				void tptAlert(t('call.micTimeout', { defaultValue: '마이크 응답이 없습니다({{sec}}초). 앱의 마이크 권한(설정 > 앱 > TPTalk > 권한)을 확인하고 다시 시도해 주세요.', sec: MIC_TIMEOUT_S }));
				return { stream: null, ok: false };
			}
			// 원인 코드 병기 — NotAllowedError(권한 거부)/NotFoundError(장치 없음)/NotReadableError(다른 앱 점유)
			const name = (e as Error)?.name || 'Error';
			void tptAlert(`${t('call.noMicProceed', { defaultValue: '마이크가 없어 내 음성 없이 연결합니다. 상대방 소리는 들립니다.' })} (${name})`);
			return { stream: null, ok: true };
		}
	}, [t]);

	// ── 발신 ──────────────────────────────────────────────────────────────
	//   mode='screen' 이면 전화가 아니라 "화면만 공유" 세션 (2026-08-06 사용자) — 마이크/음성 m-line 없이
	//   미리 캡처한 shareTrack 을 바로 실어 보낸다. 수신측은 마커 mode 로 화면공유 요청 UI 를 띄움.
	const startOutgoing = useCallback(async (peer: string, peerName?: string, mode: 'voice' | 'screen' = 'voice', shareTrack?: MediaStreamTrack) => {
		if (!peer || peer === username) return;
		if (phaseRef.current !== 'idle') { void tptAlert(t('call.alreadyInCall', { defaultValue: '이미 통화 중입니다.' })); return; }
		let stream: MediaStream | null = null;
		// 🍎 iOS 셸의 네이티브 통화 미디어 (2026-09-17) — voice 만. 마이크·PeerConnection 은 셸이 잡으므로 여기선 getUserMedia 를
		//   부르지 않는다(둘이 동시에 마이크를 잡으면 오디오 세션 충돌). 화면 전용(screen) 세션은 음성이 없어 웹 PC 경로 그대로.
		const useNative = mode === 'voice' && nativeCallAvailable();
		if (mode === 'voice' && !useNative) {
			const mic = await getMic();
			if (!mic.ok) return;
			stream = mic.stream;
			setNoMic(!stream);
			noMicRef.current = !stream;
		}
		let callId = '';
		try {
			const { rid } = await withTimeout(rcOpenDm(authToken, userId, peer), STAGE_TIMEOUT_S, t('call.stageTimeout', { defaultValue: '{{stage}} 응답 없음({{sec}}초)', stage: t('call.stageOpenDm', { defaultValue: '대화방 열기' }), sec: STAGE_TIMEOUT_S }));
			if (!rid) throw new Error('DM 방을 열 수 없습니다');
			// 상대 표시명 보강 (2026-08-07 사용자 — 도크에 id 노출) — 호출측(+시트/카드)이 이름을 못 준 경우
			// member-names(관리자 해석, 게스트 포함)로 해석. 실패 시 id 유지.
			let peerLabelName = peerName && peerName !== peer ? peerName : '';
			if (!peerLabelName) {
				try {
					const nr = await fetch(`/api/tpt/room/member-names?roomId=${encodeURIComponent(rid)}`, { headers: { 'X-Auth-Token': authToken, 'X-User-Id': userId } });
					if (nr.ok) {
						const nm = (await nr.json() as { names?: Record<string, string> }).names;
						if (nm && nm[peer]) peerLabelName = nm[peer];
					}
				} catch { /* id 폴백 */ }
			}
			const st = await withTimeout(callStart(authToken, userId, rid, peer, { mode, fromName: displayName || username }), STAGE_TIMEOUT_S, t('call.stageTimeout', { defaultValue: '{{stage}} 응답 없음({{sec}}초)', stage: t('call.stageStart', { defaultValue: '통화 요청' }), sec: STAGE_TIMEOUT_S }));
			if (st.busy) { stream?.getTracks().forEach((tr) => tr.stop()); shareTrack?.stop(); void tptAlert(t('call.peerBusy', { defaultValue: '상대방이 통화 중입니다.' })); return; }
			callId = st.callId;
			if (useNative) {
				// 🍎 네이티브 경로 — 셸이 마이크 획득·offer 생성(ICE 수집 완료본, non-trickle)까지. 실패는 state 'failed' 사유로 거절돼 아래 catch 로.
				const ns = buildNative(callId);
				nativeRef.current = ns;
				const offerSdp = await ns.createOffer();
				await withTimeout(callSignal(authToken, userId, callId, 'offer', offerSdp), STAGE_TIMEOUT_S, t('call.stageTimeout', { defaultValue: '{{stage}} 응답 없음({{sec}}초)', stage: t('call.stageSignal', { defaultValue: '연결 정보 전송' }), sec: STAGE_TIMEOUT_S }));
			} else {
				const pc = await withTimeout(buildPc(), STAGE_TIMEOUT_S, t('call.stageTimeout', { defaultValue: '{{stage}} 응답 없음({{sec}}초)', stage: t('call.stageIce', { defaultValue: '연결 설정 조회' }), sec: STAGE_TIMEOUT_S }));
				pcRef.current = pc;
				localStreamRef.current = stream;
				if (mode === 'screen' && shareTrack) {
					// 화면만 공유 — 오디오 m-line 없이 화면 트랙만. 처음부터 실어 보내 연결 즉시 표시.
					//   stream 동봉 필수 — 없으면 SDP 에 msid 가 빠져 일부 스택에서 ontrack/렌더가 누락된다 (2026-08-07 #1).
					const shareSender = pc.addTrack(shareTrack, new MediaStream([shareTrack]));
					void boostVideoSender(shareSender, shareTrack);   // 초기 저해상도 램프업 해소 (#3)
					screenTrackRef.current = shareTrack;
					shareTrack.onended = () => endCall('local');   // 브라우저 "공유 중지" = 세션 종료
					sharingRef.current = true;
					setSharing(true);
				} else {
					if (stream) stream.getTracks().forEach((tr) => pc.addTrack(tr, stream!));
					else pc.addTransceiver('audio', { direction: 'recvonly' });   // 🎤 없음 — 상대 음성 수신용 m-line 만
					pc.addTransceiver('video', { direction: 'sendrecv' });   // 화면 공유용 — 트랙 없이 협상만. (iOS 만 빼는 게이트는 2026-09-16 실측으로 스피커 고정과 무관 확인 → 원복)
				}
				const dc = pc.createDataChannel('tpt-ctl');
				dcRef.current = dc; wireDc(dc);
				const offer = await pc.createOffer();
				await pc.setLocalDescription(offer);
				await waitIce(pc);
				await withTimeout(callSignal(authToken, userId, callId, 'offer', pc.localDescription?.sdp || offer.sdp || ''), STAGE_TIMEOUT_S, t('call.stageTimeout', { defaultValue: '{{stage}} 응답 없음({{sec}}초)', stage: t('call.stageSignal', { defaultValue: '연결 정보 전송' }), sec: STAGE_TIMEOUT_S }));
			}
			// invite 마커 게시 — 수신자 벨울림 트리거(rooms-changed) + 통화 로그 카드
			const msg = await rcSendMessage(authToken, userId, rid, buildCallMarker({
				kind: 'call', callId, state: 'invite', from: username, to: peer, fromName: displayName || username, mode,
			}));
			setSession({ callId, rid, peerUsername: peer, peerName: peerLabelName || peerName || peer, role: 'caller', mode, msgId: msg?._id });
			setPhase('outgoing');
			// 🔴 ref 수동 동기화 (2026-08-07 — 화면공유 검은 화면의 진짜 근본원인). ref 는 '렌더 시점'에
			//   갱신되므로, await startOutgoing() 직후 이어지는 코드(onShare 의 트랙 정리 가드)가 아직
			//   stale 'idle' 을 읽고 방금 시작한 공유 트랙을 즉시 stop 해 버렸음(진단 state=ended 실측).
			sessionRef.current = { callId, rid, peerUsername: peer, peerName: peerLabelName || peerName || peer, role: 'caller', mode, msgId: msg?._id };
			phaseRef.current = 'outgoing';
			// 🖥 화면 공유는 전화가 아님 — 발신 대기음 없음 (수신측도 자동 수락, 0.2.288)
			stopToneRef.current = mode === 'voice' ? startTone('ringback') : null;
			// 수락/거절 폴링(1s) — answer 도착 시 연결
			pollRef.current = window.setInterval(async () => {
				const cur = await callState(authToken, userId, callId).catch(() => null);
				if (!cur || phaseRef.current !== 'outgoing') return;
				if (cur.state === 'declined') {
					stopRing(); clearPoll();
					void finalizeMarker('declined');
					void tptAlert(cur.reason === 'busy'
						? t('call.peerBusy', { defaultValue: '상대방이 통화 중입니다.' })
						: cur.reason === 'unsupported'
							? t('call.peerUnsupported', { defaultValue: '상대방의 접속 환경에서 통화를 지원하지 않습니다. (데스크톱 앱/HTTPS 필요)' })
							: t('call.declinedByPeer', { defaultValue: '상대방이 통화를 거절했습니다.' }));
					endingRef.current = true; cleanup();
					return;
				}
				if (cur.state === 'ended' || cur.state === 'cancelled') { endCall('remote'); return; }
				if (cur.state === 'accepted' && cur.answer && nativeRef.current) {
					// 🍎 네이티브 경로 — answer 적용 1회. 연결 성립은 state 'connected' 이벤트(buildNative)가 처리
					if (nativeAnswerAppliedRef.current) return;
					nativeAnswerAppliedRef.current = true;
					stopRing(); clearPoll();
					setPhase('connecting');
					nativeRef.current.setAnswer(cur.answer);
					return;
				}
				if (cur.state === 'accepted' && cur.answer && pcRef.current && !pcRef.current.currentRemoteDescription) {
					stopRing(); clearPoll();
					setPhase('connecting');
					// 비트레이트 힌트 munge — 거부 시 원본으로 1회 재시도 (#3)
					try { await pcRef.current.setRemoteDescription({ type: 'answer', sdp: boostVideoSdp(cur.answer) }); }
					catch {
						try { await pcRef.current.setRemoteDescription({ type: 'answer', sdp: cur.answer }); }
						catch { endCall('local'); }
					}
				}
			}, 1000);
			// 60s 무응답 → 부재중
			timeoutRef.current = window.setTimeout(() => { if (phaseRef.current === 'outgoing') endCall('missed'); }, 60_000);
		} catch (e) {
			stream?.getTracks().forEach((tr) => tr.stop());
			shareTrack?.stop();
			if (callId) void callEnd(authToken, userId, callId).catch(() => undefined);
			cleanup();
			void tptAlert(t('call.startFail', { defaultValue: '통화를 시작할 수 없습니다: {{msg}}', msg: nativeFailMsg(e) }));
		}
	}, [authToken, userId, username, displayName, getMic, buildPc, buildNative, nativeFailMsg, wireDc, stopRing, clearPoll, finalizeMarker, endCall, cleanup, t]);

	// ── 수신 ──────────────────────────────────────────────────────────────
	const startIncoming = useCallback(async (callId: string, rid: string, fromUsername: string, fromName?: string, mode: 'voice' | 'screen' = 'voice') => {
		if (phaseRef.current !== 'idle') {
			// 이미 통화/벨울림 중 — 같은 통화의 중복 이벤트는 무시, 다른 통화는 busy 거절
			if (sessionRef.current?.callId !== callId) void callDecline(authToken, userId, callId, 'busy').catch(() => undefined);
			return;
		}
		// 마이크 불가 환경(HTTP 웹 등 insecure context) — 보이스톡만 자동 거절.
		//   화면 공유 '보기'는 RTCPeerConnection 만 쓰므로(getUserMedia 불필요) HTTP 웹에서도 가능.
		if (mode === 'voice' && !navigator.mediaDevices?.getUserMedia && !nativeCallAvailable()) {
			void callDecline(authToken, userId, callId, 'unsupported').catch(() => undefined);
			return;
		}
		const st = await callState(authToken, userId, callId).catch(() => null);
		if (!st || st.state !== 'ringing') return;   // 이미 종료/수락된 stale 이벤트
		const sess: Session = { callId, rid, peerUsername: fromUsername, peerName: fromName || st.from.name || fromUsername, role: 'callee', mode };
		setSession(sess);
		setPhase('incoming');
		// 📱 안드로이드 알림 [받기] 로 들어온 경우 (2026-08-09) — 네이티브가 앱을 열며 callId 를 넘기는데,
		//   콜드스타트면 이 핸들러보다 먼저 도착하므로 pendingAcceptRef 에 담아 뒀다가 여기서 소비한다.
		if (pendingAcceptRef.current === callId) {
			pendingAcceptRef.current = null;
			sessionRef.current = sess;
			phaseRef.current = 'incoming';   // ref 는 렌더 후 갱신 — 즉시 수락하려면 수동 동기화 (screen 분기와 동일)
			void acceptCall();
			return;
		}
		// 🖥 화면 공유는 전화가 아님 (2026-08-07 사용자) — 벨울림/수락 없이 자동으로 보기 시작.
		//   ref 는 렌더 후에나 갱신되므로 수동 동기화 후 즉시 수락. 모달은 voice 만 렌더라 화면 깜빡임 없음.
		if (mode === 'screen') {
			sessionRef.current = sess;
			phaseRef.current = 'incoming';
			// 🛡 다기기 레이스 가드 (2026-08-07 #1) — 수신자가 여러 기기(백그라운드 APK 웹뷰·다른 탭)에
			//   로그인돼 있으면 안 보고 있는 기기가 먼저 자동 수락 → 서버가 accepted 가 되고, 정작 사용자가
			//   보는 기기는 ringing 이 아니라 조용히 무시 = "화면이 안 보임". 보이는(visible) 클라이언트만
			//   즉시 수락하고, 숨김 클라이언트는 보이게 되는 순간 아직 ringing 이면 그때 수락한다.
			if (typeof document === 'undefined' || document.visibilityState === 'visible') {
				void acceptCall();
				return;
			}
			const onVis = () => {
				if (document.visibilityState !== 'visible') return;
				document.removeEventListener('visibilitychange', onVis);
				if (phaseRef.current === 'incoming' && sessionRef.current?.callId === callId) void acceptCall();
			};
			document.addEventListener('visibilitychange', onVis);
			// 발신 취소/타기기 수락 감지 — ringing 을 벗어나면 조용히 정리 (수신측 카드는 발신자가 수정)
			pollRef.current = window.setInterval(async () => {
				if (phaseRef.current !== 'incoming') { document.removeEventListener('visibilitychange', onVis); clearPoll(); return; }
				const cur = await callState(authToken, userId, callId).catch(() => null);
				if (cur && cur.state === 'ringing') return;
				document.removeEventListener('visibilitychange', onVis);
				clearPoll();
				endingRef.current = true; cleanup();
			}, 2000);
			timeoutRef.current = window.setTimeout(() => {
				document.removeEventListener('visibilitychange', onVis);
				if (phaseRef.current === 'incoming') { endingRef.current = true; cleanup(); }
			}, 90_000);
			return;
		}
		stopToneRef.current = startTone('incoming');
		// 발신 취소 감지 폴링 — 다른 기기/탭에서 수락된 경우(accepted)도 이 UI 정리
		pollRef.current = window.setInterval(async () => {
			if (phaseRef.current !== 'incoming') return;
			const cur = await callState(authToken, userId, callId).catch(() => null);
			if (cur && cur.state === 'ringing') return;
			stopRing(); clearPoll();
			endingRef.current = true; cleanup();       // 부재중 카드는 발신자가 수정 — 수신측은 조용히 정리
		}, 1000);
		timeoutRef.current = window.setTimeout(() => { if (phaseRef.current === 'incoming') { endingRef.current = true; cleanup(); } }, 90_000);
	}, [authToken, userId, stopRing, clearPoll, cleanup]);

	const acceptCall = useCallback(async () => {
		const s = sessionRef.current;
		if (!s || phaseRef.current !== 'incoming') return;
		stopRing(); clearPoll();
		// 🕐 즉시 '연결 중…' 도크 표시 (2026-08-22) — 받기 클릭 후 마이크 획득·SDP 교환 동안 벨 모달이
		//   그대로면 눌린 건지 알 수 없다. 통화 타이머는 실제 음성 도달(activateIfConnecting)부터 시작.
		setPhase('connecting');
		phaseRef.current = 'connecting';
		// 화면 공유 보기 — 마이크 불필요(음성 m-line 자체가 없음)
		let stream: MediaStream | null = null;
		const useNative = s.mode === 'voice' && nativeCallAvailable();   // 🍎 마이크는 셸이 잡는다 (startOutgoing 과 동일 규칙)
		if (s.mode === 'voice' && !useNative) {
			const mic = await getMic();
			if (!mic.ok) {
				void callDecline(authToken, userId, s.callId, 'unsupported').catch(() => undefined);
				endingRef.current = true; cleanup();
				return;
			}
			stream = mic.stream;
			setNoMic(!stream);
			noMicRef.current = !stream;
		}
		try {
			await callAccept(authToken, userId, s.callId);
		} catch {
			// 🛡 같은 계정의 다른 기기가 먼저 수락(409)했거나 발신자가 방금 취소한 경우 —
			//   여기서 callEnd 를 쏘면 이긴 기기의 통화까지 끊기므로 이 기기 UI 만 조용히 정리 (2026-08-07 #1).
			stream?.getTracks().forEach((tr) => tr.stop());
			endingRef.current = true; cleanup();
			return;
		}
		try {
			const st = await callState(authToken, userId, s.callId);
			if (!st?.offer) throw new Error('offer 없음');
			if (useNative) {
				// 🍎 네이티브 경로 — 셸이 마이크 획득·상대 offer(video m-line 포함, inactive 응답) 수용·answer 생성. 연결 성립은 state 'connected'
				const ns = buildNative(s.callId);
				nativeRef.current = ns;
				setPhase('connecting');
				const answerSdp = await ns.acceptOffer(st.offer);
				await callSignal(authToken, userId, s.callId, 'answer', answerSdp);
			} else {
				const pc = await buildPc();
				pcRef.current = pc;
				localStreamRef.current = stream;
				setPhase('connecting');
				// 비트레이트 힌트 munge — 거부 시 원본으로 재시도 (#3, 통화 중 공유의 수신자→발신자 방향)
				try { await pc.setRemoteDescription({ type: 'offer', sdp: boostVideoSdp(st.offer) }); }
				catch { await pc.setRemoteDescription({ type: 'offer', sdp: st.offer }); }
				// 🎤 없어도 진행 — addTrack 생략 시 audio m-line 은 수신 전용(상대 소리만 들림)
				if (stream) stream.getTracks().forEach((tr) => pc.addTrack(tr, stream));
				// offer 의 video m-line 을 양방향으로 — 내 쪽에서도 화면 공유 send 가능하게
				for (const tx of pc.getTransceivers()) {
					if (tx.receiver.track?.kind === 'video') { try { tx.direction = 'sendrecv'; } catch { /* */ } }
				}
				const answer = await pc.createAnswer();
				await pc.setLocalDescription(answer);
				await waitIce(pc);
				await callSignal(authToken, userId, s.callId, 'answer', pc.localDescription?.sdp || answer.sdp || '');
			}
			// 연결 성립은 onconnectionstatechange(🍎 네이티브는 state 이벤트)가 처리. 25s 내 미연결이면 실패 종료.
			timeoutRef.current = window.setTimeout(() => {
				if (phaseRef.current === 'connecting') {
					void tptAlert(t('call.connectFail', { defaultValue: '통화 연결에 실패했습니다. 네트워크를 확인해 주세요.' }));
					endCall('local');
				}
			}, 25_000);
		} catch (e) {
			stream?.getTracks().forEach((tr) => tr.stop());
			void callEnd(authToken, userId, s.callId).catch(() => undefined);
			endingRef.current = true; cleanup();
			void tptAlert(t('call.startFail', { defaultValue: '통화를 시작할 수 없습니다: {{msg}}', msg: nativeFailMsg(e) }));
		}
	}, [authToken, userId, getMic, buildPc, buildNative, nativeFailMsg, stopRing, clearPoll, cleanup, endCall, t]);

	const declineCall = useCallback(() => {
		const s = sessionRef.current;
		if (!s) return;
		void callDecline(authToken, userId, s.callId).catch(() => undefined);
		endingRef.current = true;   // 거절 마커는 발신자 폴링이 수정 — 수신측은 정리만
		cleanup();
	}, [authToken, userId, cleanup]);

	// ── 통화 중 제어 ────────────────────────────────────────────────────────
	const toggleMute = useCallback(() => {
		const next = !muted;
		if (nativeRef.current) {
			nativeRef.current.setMuted(next);   // 🍎 마이크는 셸이 쥐고 있다
		} else {
			const track = localStreamRef.current?.getAudioTracks()[0];
			if (!track) return;
			track.enabled = !next;
		}
		setMuted(next);
		dcSend({ t: 'mute', on: next });
	}, [muted]);

	// 🔊 스피커폰 ↔ 수화기 (네이티브 앱 전용 — 안드로이드·iOS 모두 setSpeaker 브릿지를 가진다)
	//   setInCall 은 **기본 수화기**로 켠다(code 33, 2026-08-07 사용자 결정 — MainActivity.routeCallAudio(am,false),
	//   iOS 는 overrideOutputAudioPort(.none)). 그래서 speakerOn 초기값도 false 다.
	const toggleSpeaker = useCallback(() => {
		setSpeakerOn((cur) => {
			const next = !cur;
			try {
				if (nativeRef.current) nativeRef.current.setSpeaker(next);   // 🍎 네이티브 경로 — 오디오 세션 주인이 셸(구 setSpeaker 는 웹뷰 경로용)
				else window.TPTAndroid?.setSpeaker?.(next);
			} catch { /* 브릿지 없음 */ }
			return next;
		});
	}, []);

	const videoSender = (): RTCRtpSender | null => {
		const pc = pcRef.current;
		if (!pc) return null;
		for (const tx of pc.getTransceivers()) {
			if (tx.sender.track?.kind === 'video') return tx.sender;
			if (!tx.sender.track && tx.receiver.track?.kind === 'video') return tx.sender;
		}
		return null;
	};

	const stopShare = useCallback(() => {
		// 🖥 화면만 공유 세션 — 공유 중지 = 세션 종료 (공유 없는 빈 세션은 무의미)
		if (sessionRef.current?.mode === 'screen') { endCall('local'); return; }
		const sender = videoSender();
		try { nativeCapStopRef.current?.(); } catch { /* */ }
		nativeCapStopRef.current = null;
		try { screenTrackRef.current?.stop(); } catch { /* */ }
		screenTrackRef.current = null;
		void sender?.replaceTrack(null).catch(() => undefined);
		sharingRef.current = false;
		setSharing(false);
		setCapMode(null);
		dcSend({ t: 'screen', on: false });
	}, [endCall]);

	// 화면 트랙을 통화의 video sender 에 부착 — 도크 버튼(startShare)과 + 시트 자동 공유가 공용
	const attachShareTrack = useCallback(async (track: MediaStreamTrack) => {
		const sender = videoSender();
		if (!sender || phaseRef.current !== 'active') { track.stop(); return; }
		try { await sender.replaceTrack(track); } catch { track.stop(); return; }
		void boostVideoSender(sender, track);   // 초기 저해상도 램프업 해소 (#3)
		screenTrackRef.current = track;
		track.onended = () => stopShare();   // 브라우저 "공유 중지" 버튼과 동기화
		sharingRef.current = true;
		setSharing(true);
		dcSend({ t: 'screen', on: true });
	}, [stopShare]);

	// 공유 대상 선택 — 데스크톱: Rust 목록(모니터+창)으로 자체 픽커 모달. 반환: 대상 / 'browser'(웹 경로) / null(취소)
	const pickCaptureSource = useCallback(async (): Promise<CaptureSource | 'browser' | null> => {
		if (!isTauri()) return 'browser';
		let sources: CaptureSource[] = [];
		try {
			const { invoke } = await import('@tauri-apps/api/core');
			sources = await invoke<CaptureSource[]>('screen_sources');
		} catch { /* 목록 실패 — 주 모니터 폴백 */ }
		const monitors = sources.filter((s) => s.kind === 'monitor');
		if (sources.length === 0) return { kind: 'monitor', id: 0, name: '', width: 0, height: 0 };   // 폴백: 주 모니터(id 무시)
		if (monitors.length === 1 && sources.length === 1) return monitors[0];   // 선택지 1개 — 픽커 생략
		// 🖼 썸네일 로드·해제는 공용 픽커(ScreenSourcePicker)가 맡는다 (2026-09-04 공용화)
		return await new Promise<CaptureSource | null>((resolve) => {
			const done = (v: CaptureSource | null) => { setSharePicker(null); resolve(v); };
			setSharePicker({ sources, proceed: done, cancel: () => done(null) });
		});
	}, []);

	// 화면 트랙 획득 — 데스크톱(Tauri)은 Rust 네이티브 캡처(웹뷰 getDisplayMedia 는 프레임 0 실증 —
	// 실패 시 조용한 폴백 대신 사유를 알림으로 노출). 그 외 환경은 getDisplayMedia.
	const acquireScreenTrack = useCallback(async (target?: CaptureSource): Promise<MediaStreamTrack | null> => {
		if (isTauri()) {
			const nt = await startNativeScreenTrack(target);
			if (nt) { nativeCapStopRef.current = nt.stop; setCapMode('native'); return nt.track; }
			void tptAlert(t('call.nativeCaptureFail', { defaultValue: '화면 캡처를 시작하지 못했습니다: {{msg}}', msg: _lastNativeCapErr || 'unknown' }));
			return null;
		}
		if (!navigator.mediaDevices?.getDisplayMedia) return null;
		const ds = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
		const tr = ds.getVideoTracks()[0] || null;
		if (tr) setCapMode('browser');
		return tr;
	}, [t]);

	const startShare = useCallback(async () => {
		if (phaseRef.current !== 'active' || sharingRef.current) return;
		try {
			const sel = await pickCaptureSource();
			if (sel === null) return;   // 픽커에서 취소 — 조용히
			const track = await acquireScreenTrack(sel === 'browser' ? undefined : sel);
			if (!track) {
				void tptAlert(t('call.shareUnsupported', { defaultValue: '이 환경에서는 화면 공유를 지원하지 않습니다.' }));
				return;
			}
			await attachShareTrack(track);
		} catch { /* 사용자가 화면 선택 취소 — 무시 */ }
	}, [pickCaptureSource, acquireScreenTrack, attachShareTrack, t]);

	// ── 이벤트 수신 (발신 시작 / 수신 벨울림) ────────────────────────────────
	useEffect(() => {
		const onStart = (e: Event) => {
			const d = (e as CustomEvent<{ username?: string; name?: string }>).detail;
			if (d?.username) void startOutgoing(d.username, d.name);
		};
		const onIncoming = (e: Event) => {
			const d = (e as CustomEvent<{ callId?: string; rid?: string; fromUsername?: string; fromName?: string; mode?: 'voice' | 'screen' }>).detail;
			if (d?.callId && d.rid && d.fromUsername) void startIncoming(d.callId, d.rid, d.fromUsername, d.fromName, d.mode === 'screen' ? 'screen' : 'voice');
		};
		// 🖥 + 시트 "화면 공유" — 보이스톡 통화 중이면 그 통화에서 공유 토글, 아니면 전화 없이
		//   "화면만 공유" 세션(mode='screen') 시작: 화면 선택(제스처 안 getDisplayMedia 필수) → 공유 요청 발신.
		//   dispatchEvent 는 동기 실행이라 클릭 제스처가 유지됨.
		const onShare = (e: Event) => {
			const d = (e as CustomEvent<{ username?: string; name?: string }>).detail;
			if (!d?.username) return;
			if (phaseRef.current === 'active') {
				if (sessionRef.current?.peerUsername !== d.username) { void tptAlert(t('call.alreadyInCall', { defaultValue: '이미 통화 중입니다.' })); return; }
				if (sharingRef.current) stopShare(); else void startShare();
				return;
			}
			if (phaseRef.current !== 'idle') { void tptAlert(t('call.alreadyInCall', { defaultValue: '이미 통화 중입니다.' })); return; }
			void (async () => {
				try {
					const sel = await pickCaptureSource();
					if (sel === null) return;   // 픽커에서 취소 — 조용히
					const track = await acquireScreenTrack(sel === 'browser' ? undefined : sel);
					if (!track) { void tptAlert(t('call.shareUnsupported', { defaultValue: '이 환경에서는 화면 공유를 지원하지 않습니다.' })); return; }
					await startOutgoing(d.username!, d.name, 'screen', track);
					// 발신 실패(상대 busy 등)로 idle 로 남으면 화면 트랙·네이티브 캡처 정리 (성공 시 cleanup 이 관리)
					if (phaseRef.current === 'idle') {
						try { nativeCapStopRef.current?.(); } catch { /* */ }
						nativeCapStopRef.current = null;
						if (track.readyState === 'live') track.stop();
					}
				} catch { /* 화면 선택 취소 — 무시 */ }
			})();
		};
		window.addEventListener('tpt:call-start', onStart as EventListener);
		window.addEventListener('tpt:call-incoming', onIncoming as EventListener);
		window.addEventListener('tpt:call-share', onShare as EventListener);
		return () => {
			window.removeEventListener('tpt:call-start', onStart as EventListener);
			window.removeEventListener('tpt:call-incoming', onIncoming as EventListener);
			window.removeEventListener('tpt:call-share', onShare as EventListener);
		};
	}, [startOutgoing, startIncoming, startShare, stopShare, acquireScreenTrack, pickCaptureSource, t]);

	// 📱 안드로이드 알림 [받기] (2026-08-09) — 네이티브가 앱을 열며 'tpt:call-accept' {callId} 를 보낸다.
	//   이미 벨울림 중이면 즉시 수락, 아직이면 pendingAcceptRef 에 담아 두고 startIncoming 이 소비한다
	//   (콜드스타트는 이 이벤트가 invite 수신보다 먼저 도착). 60초 지나면 무효화 — 지난 통화 오수락 방지.
	useEffect(() => {
		const hold = (cid: string, at = Date.now()) => {
			pendingAcceptRef.current = cid;
			window.setTimeout(() => { if (pendingAcceptRef.current === cid) pendingAcceptRef.current = null; }, Math.max(0, 60_000 - (Date.now() - at)));
		};
		const onAccept = (e: Event) => {
			const cid = String((e as CustomEvent).detail?.callId || '').trim();
			if (!cid) return;
			try { (window as unknown as { __tptPendingCallAccept?: unknown }).__tptPendingCallAccept = null; } catch { /* 이벤트로 받았으니 전역 사본은 버린다 */ }
			if (sessionRef.current?.callId === cid && phaseRef.current === 'incoming') { void acceptCall(); return; }
			hold(cid);
		};
		// 📱 콜드스타트 [받기] (2026-09-30 형 "알림에서 받기 누르면, 앱 들어가서 다시 받기 눌러야 함") — 앱이 꺼져 있다 [받기]로 뜨면 셸이
		//   페이지 로드 직후(onPageFinished) 수락 신호를 쏘는데, 그때는 로그인·화면 준비 전이라 이 리스너가 아직 없어 신호가 사라졌다.
		//   그 뒤 놓친 벨 복구가 벨 화면을 띄워 한 번 더 눌러야 했다 → 셸(code 115)이 전역에도 남겨 두고, 마운트 때 60초 안이면 이어받는다.
		try {
			const w = window as unknown as { __tptPendingCallAccept?: { callId?: string; at?: number } | null };
			const p = w.__tptPendingCallAccept;
			w.__tptPendingCallAccept = null;
			if (p?.callId && typeof p.at === 'number' && Date.now() - p.at < 60_000) hold(String(p.callId), p.at);
		} catch { /* */ }
		window.addEventListener('tpt:call-accept', onAccept as EventListener);
		return () => window.removeEventListener('tpt:call-accept', onAccept as EventListener);
	}, [acceptCall]);
	// 📱 보관된 [받기]를 벨 화면이 뜨는 순간에도 소비 — 수락 신호와 초대 수신의 순서가 어떻든(startIncoming 이 먼저 끝났든) 한 번 더 누르지 않게
	useEffect(() => {
		if (phase !== 'incoming' || !session || pendingAcceptRef.current !== session.callId) return;
		pendingAcceptRef.current = null;
		void acceptCall();
	}, [phase, session, acceptCall]);

	// 📴 발신 취소 마커 기반 벨 정지 (2026-08-22 사용자 "발신자가 끊어도 수신음이 계속") — 발신자가 벨울림 중
	//   취소하면 invite 마커가 missed 로 수정돼 DDP 로 즉시 push 되고, useRealtimeNotify 가 'tpt:call-ended'
	//   {callId} 로 중계한다. REST 1s 폴링과 독립한 2번째 정지 경로 — 같은 통화의 벨울림 중일 때만 반응.
	useEffect(() => {
		const onEnded = (e: Event) => {
			const cid = String((e as CustomEvent<{ callId?: string }>).detail?.callId || '').trim();
			if (!cid || phaseRef.current !== 'incoming' || sessionRef.current?.callId !== cid) return;
			stopRing(); clearPoll();
			endingRef.current = true; cleanup();
		};
		window.addEventListener('tpt:call-ended', onEnded as EventListener);
		return () => window.removeEventListener('tpt:call-ended', onEnded as EventListener);
	}, [stopRing, clearPoll, cleanup]);

	// 언마운트(로그아웃 등) 시 통화 정리
	useEffect(() => () => { try { pcRef.current?.close(); } catch { /* */ } cleanup(); }, [cleanup]);

	// 통화 타이머 — active 동안 1s 리렌더
	useEffect(() => {
		if (phase !== 'active') return;
		const iv = window.setInterval(() => setTick((n) => n + 1), 1000);
		return () => window.clearInterval(iv);
	}, [phase]);
	void tick;

	// 📊 미디어 흐름 진단 (2026-08-07 #2 — "연결됐는데 소리/화면이 없다" 즉석 판별) — active 동안 4초마다
	//   getStats 로 방향별 바이트 증가를 측정, 멈춘 방향만 도크에 경고 + diagLog(로그인 화면 진단 로그로 수집 가능).
	//   첫 틱은 기준선(경고 없음). 종료 시 초기화.
	useEffect(() => {
		if (phase !== 'active') { setDiag(null); return; }
		let prev = { aIn: 0, aOut: 0, vIn: 0, vOut: 0 };
		let first = true;
		const iv = window.setInterval(async () => {
			// 🍎 네이티브 경로 — 셸의 stats 이벤트(bytes 누계)로 오디오 방향만 판정. 이벤트가 없으면 진단 생략(경고 없음)
			if (nativeRef.current) {
				const ls = nativeRef.current.lastStats;
				if (!ls) return;
				const d = { aIn: ls.aIn > prev.aIn, aOut: ls.aOut > prev.aOut, vIn: true, vOut: true };
				prev = { aIn: ls.aIn, aOut: ls.aOut, vIn: 0, vOut: 0 };
				if (first) { first = false; return; }
				diagLog('call stats', `a↑${ls.aOut} a↓${ls.aIn} (native)`);
				setDiag(d);
				return;
			}
			const pc = pcRef.current;
			if (!pc) return;
			try {
				const stats = await pc.getStats();
				const cur = { aIn: 0, aOut: 0, vIn: 0, vOut: 0 };
				let srcFrames = -1;   // media-source(video).frames — 캡처가 만든 원본 프레임 수
				let encFrames = -1;   // outbound-rtp(video).framesEncoded — 인코더가 소비한 프레임 수
				stats.forEach((r) => {
					const rec = r as unknown as { type?: string; kind?: string; bytesSent?: number; bytesReceived?: number; frames?: number; framesEncoded?: number };
					if (rec.type === 'outbound-rtp') {
						if (rec.kind === 'audio') cur.aOut += rec.bytesSent || 0;
						else if (rec.kind === 'video') { cur.vOut += rec.bytesSent || 0; encFrames = rec.framesEncoded ?? encFrames; }
					} else if (rec.type === 'inbound-rtp') {
						if (rec.kind === 'audio') cur.aIn += rec.bytesReceived || 0;
						else if (rec.kind === 'video') cur.vIn += rec.bytesReceived || 0;
					} else if (rec.type === 'media-source' && rec.kind === 'video') {
						srcFrames = rec.frames ?? srcFrames;
					}
				});
				const d = { aIn: cur.aIn > prev.aIn, aOut: cur.aOut > prev.aOut, vIn: cur.vIn > prev.vIn, vOut: cur.vOut > prev.vOut };
				prev = cur;
				if (first) { first = false; return; }
				diagLog('call stats', `a↑${cur.aOut} a↓${cur.aIn} v↑${cur.vOut} v↓${cur.vIn}`);
				// 🖥 화면 송신 정밀 진단 — srcFrames=0(캡처가 프레임 자체를 못 만듦=웹뷰 캡처 문제) vs
				//   srcFrames>0 & encFrames=0(인코더/협상 문제) vs 둘 다 증가(전송·수신측 문제) 구분용.
				if (sharingRef.current) {
					const tr = screenTrackRef.current;
					diagLog('share diag', `srcFrames=${srcFrames} encFrames=${encFrames} muted=${tr?.muted} state=${tr?.readyState} enabled=${tr?.enabled}`);
					setShareDbg(`src=${srcFrames} enc=${encFrames} muted=${tr?.muted ? 1 : 0}`);
				}
				setDiag(d);
			} catch { /* getStats 미지원 — 진단 생략 */ }
		}, 4000);
		return () => window.clearInterval(iv);
	}, [phase]);

	// 🖐 통화 도크 끌어 옮기기 (2026-09-10) — 세션이 끝나면 원위치(다음 통화는 설정 위치에서 시작)
	const dockDrag = useDragOffset();
	const dockDragReset = dockDrag.reset;
	useEffect(() => { if (phase === 'idle') dockDragReset(); }, [phase, dockDragReset]);

	// 🔁 ICE 재시작(발신자만, 2026-10-06) — 끊김 감지 시 새 offer(iceRestart)를 서버에 reoffer 로 올린다. 수신자는 아래 3s 폴링으로 받아 reanswer.
	//   네이티브(iOS) 경로·진행 중 중복·통화당 5회 초과는 건너뛴다. 실패해도 유예 타이머가 종전처럼 통화를 정리한다.
	iceRestartRef.current = () => {
		const pc = pcRef.current;
		const s = sessionRef.current;
		if (!pc || !s || s.role !== 'caller' || nativeRef.current || restartingRef.current || restartsRef.current >= 5) return;
		if (pc.signalingState !== 'stable') return;
		restartingRef.current = true;
		restartsRef.current += 1;
		void (async () => {
			try {
				const offer = await pc.createOffer({ iceRestart: true });
				await pc.setLocalDescription(offer);
				await waitIce(pc);
				const r = await callSignal(authToken, userId, s.callId, 'reoffer', pc.localDescription?.sdp || '');
				reSeqRef.current = r.seq || 0;
				diagLog('call ice-restart', `seq=${r.seq || 0} n=${restartsRef.current}`);
			} catch (e) { diagLog('call ice-restart fail', String((e as Error).message || e)); }
			finally { restartingRef.current = false; }
		})();
	};

	// 🔚 통화 상태 서버 폴링 (2026-08-07 #2 — 한쪽 종료가 상대에 전파 안 되는 케이스 안전망) — active 중 3s.
	//   dc 'bye'/connectionstate 가 1차, 이건 신호 유실·크래시 잔재 대비 2차 (참여자 2명 × 3s 라 부하 미미).
	//   🔁 같은 폴링으로 재연결 신호도 주고받는다(2026-10-06) — 수신자: 새 회차 reoffer 적용 → reanswer / 발신자: 내 회차 reanswer 적용.
	useEffect(() => {
		if (phase !== 'active' || !session?.callId) return;
		const cid = session.callId;
		let busy = false;
		const iv = window.setInterval(async () => {
			if (busy) return;
			busy = true;
			try {
				const cur = await callState(authToken, userId, cid).catch(() => null);
				if (cur && (cur.state === 'ended' || cur.state === 'cancelled' || cur.state === 'declined')) { endDetailRef.current = `server-${cur.state}`; endCall('remote'); return; }
				const s = sessionRef.current;
				const pc = pcRef.current;
				if (!cur || !s || !pc || nativeRef.current || !cur.reSeq) return;
				if (s.role === 'callee' && cur.reoffer && cur.reSeq > reSeqRef.current) {
					reSeqRef.current = cur.reSeq;
					try {
						await pc.setRemoteDescription({ type: 'offer', sdp: cur.reoffer });
						const ans = await pc.createAnswer();
						await pc.setLocalDescription(ans);
						await waitIce(pc);
						await callSignal(authToken, userId, cid, 'reanswer', pc.localDescription?.sdp || '', cur.reSeq);
						diagLog('call ice-restart answered', `seq=${cur.reSeq}`);
					} catch (e) { diagLog('call ice-restart answer fail', String((e as Error).message || e)); }
				} else if (s.role === 'caller' && cur.reanswer && cur.reSeq === reSeqRef.current && pc.signalingState === 'have-local-offer') {
					try { await pc.setRemoteDescription({ type: 'answer', sdp: cur.reanswer }); diagLog('call ice-restart applied', `seq=${cur.reSeq}`); }
					catch (e) { diagLog('call ice-restart apply fail', String((e as Error).message || e)); }
				}
			} finally { busy = false; }
		}, 3000);
		return () => window.clearInterval(iv);
	}, [phase, session?.callId, authToken, userId, endCall]);

	// 👤 상대 조직 정보 — 벨울림/통화 화면에 회사·부서·직책 표시 (2026-08-07 사용자)
	useEffect(() => {
		const u = session?.peerUsername;
		if (!u) { setPeerWhois(null); return; }
		let cancel = false;
		void rcGwWhois({ username: u })
			.then((w) => { if (!cancel && w?.found) setPeerWhois(w); })
			.catch(() => undefined);   // 준회원 뷰어 403 등 — 이름만 표시
		return () => { cancel = true; };
		// i18n.language — 선호언어 변경 시 상대 조직정보를 해당 언어로 재조회 (2026-08-11)
	}, [session?.peerUsername, i18n.language]);

	// 📱 APK 볼륨키 — 통화 중에는 글자크기 대신 시스템(미디어) 볼륨으로 (code 27 브릿지, 2026-08-07 사용자)
	//   🔴 deps 는 phase 가 아니라 '통화 중 여부' 하나 (2026-10-13 끊김 보완) — 종전엔 단계가 바뀔 때마다(벨→연결 중→통화 중) cleanup 이
	//   setInCall(false)→(true) 를 보내 셸이 오디오 모드·스피커·통화 알림 경과 시간을 매번 초기화했고, code 123+ 는 통화 포그라운드 서비스를
	//   껐다 켜게 된다 — 앱이 백그라운드면 Android 14 가 재시작을 막아 서비스 없이 통화가 이어지고 1분 안팎에 끊긴다.
	const inCallFlag = phase !== 'idle';
	useEffect(() => {
		if (!inCallFlag) return;
		try { window.TPTAndroid?.setInCall?.(true); } catch { /* 웹/데탑 — 브릿지 없음 */ }
		return () => { try { window.TPTAndroid?.setInCall?.(false); } catch { /* */ } };
	}, [inCallFlag]);
	// 📞 안드 통화 중 상시 알림 (셸 code 115, 2026-09-30 형 "iOS CallKit 처럼 — 앱을 끄니까 통화중인지를 모르겠음") — 연결 중·통화 중에
	//   상태바에 '보이스톡 통화 중 · 상대 · 경과 시간 · [끊기]'. 내리는 건 위 setInCall(false). 위 effect 뒤에 둬야 셸의 inCall 이 먼저 켜진다.
	useEffect(() => {
		if (phase !== 'connecting' && phase !== 'active') return;
		try { window.TPTAndroid?.setCallOngoing?.(session?.peerName || '', session?.rid || ''); } catch { /* 웹/데탑·구 셸 — 브릿지 없음 */ }
	}, [phase, session?.peerName, session?.rid]);
	// 📞 통화 중 알림 [끊기] — 셸이 'tpt:call-hangup' 으로 넘긴다. 벨울림(incoming)은 알림 [거절]이 따로 담당.
	useEffect(() => {
		const onHang = () => { if (phaseRef.current !== 'idle' && phaseRef.current !== 'incoming') endCall('local'); };
		window.addEventListener('tpt:call-hangup', onHang);
		return () => window.removeEventListener('tpt:call-hangup', onHang);
	}, [endCall]);

	const durText = (() => {
		const at = session?.startedAt;
		if (!at) return '0:00';
		const s = Math.floor((Date.now() - at) / 1000);
		const m = Math.floor(s / 60), ss = s % 60;
		return `${m}:${String(ss).padStart(2, '0')}`;
	})();

	const peerLabel = session?.peerName || session?.peerUsername || '';

	// 👤 상대 조직 정보 한 줄 — 회사 · 부서 · 직책(직급) (2026-09-04 원격 "직책옆에 직급도 표시해줘").
	//   종전엔 `title || position` 이라 **직책이 있으면 직급이 안 보였다**. 표기 규칙은 사이드바·조직도와
	//   같은 formatNameRole 을 이름 없이 호출해 재사용한다 → "팀장(책임)" 형식으로 통일.
	//   🔴 이 줄은 대기 화면·통화 중 두 곳에서 쓰인다 — 한 곳에서 만들어 두 곳이 어긋나지 않게 한다.
	const peerOrgLine = peerWhois
		? [peerWhois.company, peerWhois.deptName, formatNameRole({ title: peerWhois.title, position: peerWhois.position })].filter(Boolean).join(' · ')
		: '';

	// 📊 진단 경고 — '흘러야 하는' 방향이 멈춘 경우만 (내 음소거/상대 음소거·무마이크는 기대상 무음이라 제외)
	const warnAudioOut = !!(diag && !diag.aOut && session?.mode === 'voice' && !noMic && !muted);
	const warnAudioIn = !!(diag && !diag.aIn && session?.mode === 'voice' && !peerNoMic && !peerMuted);
	const iosVoiceOnly = isIosApp();   // 🍎 iOS 는 화면 공유 송신이 구조적으로 불가(getDisplayMedia 없음) — 송신 경고는 소음이라 숨김 (2026-09-16, 맥 (13))
	const warnVideoOut = !!(diag && !diag.vOut && sharing && !iosVoiceOnly);
	const warnVideoIn = !!(diag && !diag.vIn && remoteScreenOn);   // 수신 경고는 iOS 도 유효 — m-line 원복으로 화면 수신이 다시 가능
	const anyDiagWarn = warnAudioOut || warnAudioIn || warnVideoOut || warnVideoIn;

	return (
		<>
			{/* 원격 오디오 — 항상 mount (ontrack 시 srcObject 만 갱신) */}
			<audio ref={audioRef} autoPlay data-testid="call-audio" />

			{/* 🖥 공유 대상 선택 모달 — 전체 화면(모니터별) + 창 목록 (2026-08-07 사용자) */}
			{/* 공유 대상 픽커 — 공용 ScreenSourcePicker (화면 캡처와 같은 모달, 2026-09-04) */}
			{sharePicker && (
				<ScreenSourcePicker sources={sharePicker.sources} title={t('call.sharePickTitle', { defaultValue: '공유할 화면 선택' })}
					onPick={sharePicker.proceed} onCancel={sharePicker.cancel} />
			)}

			{/* 수신 벨울림 모달 — 보이스톡만 (화면 공유는 자동 수락이라 벨울림 없음) */}
			{/* 📍 위치 = 개인설정 '데스크톱 알림 위치'(기본 우하단) — 통화 도크와 같은 모서리 (2026-09-04 원격). 종전 화면 중앙+어둡게 → 모서리 카드 */}
			{phase === 'incoming' && session && session.mode === 'voice' && (
				<div role="dialog" aria-modal="true" data-testid="call-incoming"
					className={`${cornerClass(getNotifPrefs().desktopPos)} z-[2400] p-1`}>
					<div className="flex w-[min(340px,92vw)] flex-col items-center rounded-3xl bg-surface px-6 py-8 shadow-2xl ring-1 ring-slate-200">
						<div className="relative mb-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-emerald-100 text-5xl">
							<Icon name="user" size={44} />
							<img src={avatarUrl(session.peerUsername)} alt=""
								className="absolute inset-0 h-full w-full object-cover"
								onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
						</div>
						<div className="text-ui-lg font-bold text-fg-emphasis">{peerLabel}</div>
						{/* 👤 발신자 조직 정보 — 회사 · 부서 · 직책 (2026-08-07 사용자) */}
						{peerWhois && (
							<div className="mt-0.5 max-w-full truncate px-2 text-ui-xs text-fg-subtle">
								{peerOrgLine}
							</div>
						)}
						<div className="mt-1 inline-flex animate-pulse items-center gap-1 text-ui-sm text-fg-subtle"><Icon name="phone" size={14} />{t('call.incomingTitle', { defaultValue: '보이스톡 수신 중…' })}</div>
						<div className="mt-7 flex items-center gap-8">
							<button type="button" onClick={declineCall} data-testid="call-decline"
								className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-2xl text-white shadow-lg hover:bg-red-600 active:scale-95"
								title={t('call.decline', { defaultValue: '거절' })}>✕</button>
							<button type="button" onClick={() => void acceptCall()} data-testid="call-accept"
								className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-2xl text-white shadow-lg hover:bg-emerald-600 active:scale-95"
								title={t('call.accept', { defaultValue: '수락' })}><Icon name="phone" size={24} /></button>
						</div>
						<div className="mt-3 flex w-full justify-between px-3 text-ui-xs text-fg-faint">
							<span>{t('call.decline', { defaultValue: '거절' })}</span>
							<span>{t('call.accept', { defaultValue: '수락' })}</span>
						</div>
					</div>
				</div>
			)}

			{/* 통화/공유 도크 — 발신 벨울림/연결/통화 중. 모바일은 하단 탭바 위.
			    🖥 화면 공유 세션은 전화 느낌 제거 — 아바타 대신 방향이 명확한 문구(내 화면 공유 중 / ○○님의 화면) */}
			{(phase === 'outgoing' || phase === 'connecting' || phase === 'active') && session && (
				<div data-testid="call-dock" data-drag-root style={dockDrag.style}
					className={`${cornerClass(getNotifPrefs().desktopPos)} z-[2400] w-72 rounded-2xl bg-slate-900/95 p-4 text-white shadow-2xl ring-1 ring-white/10`}>
					{/* 🖐 헤더(아바타·이름)를 끌면 도크가 옮겨진다 (2026-09-10 사용자 "앱 보이스톡 위치 이동") — 버튼 줄은 손잡이가 아님 */}
					<div className="flex cursor-grab items-center gap-3 active:cursor-grabbing" {...dockDrag.handleProps} data-testid="call-dock-handle">
						{session.mode === 'screen' ? (
							<span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-class-blue/30 text-xl"><Icon name="monitor" size={20} /></span>
						) : (
							<div className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-emerald-800 text-xl">
								<Icon name="user" size={20} />
								<img src={avatarUrl(session.peerUsername)} alt=""
									className="absolute inset-0 h-full w-full object-cover"
									onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
							</div>
						)}
						<div className="min-w-0 flex-1">
							<div className="truncate text-ui-sm font-bold">
								{session.mode === 'screen'
									? (session.role === 'caller'
										? t('call.sharingTo', { defaultValue: '{{name}}님에게 내 화면 공유 중', name: peerLabel })
										: t('call.screenOf', { defaultValue: '{{name}}님의 화면', name: peerLabel }))
									: peerLabel}
							</div>
							{/* 👤 상대 조직 정보 — 회사 · 부서 · 직책 (whois, 2026-08-07 사용자) */}
							{peerWhois && (
								<div className="truncate text-ui-xs text-white/50">
									{peerOrgLine}
								</div>
							)}
							<div className="text-ui-xs text-emerald-300">
								{phase === 'outgoing' && <span className="animate-pulse">{t('call.connecting', { defaultValue: '연결 중…' })}</span>}
								{phase === 'connecting' && <span className="animate-pulse">{t('call.connecting', { defaultValue: '연결 중…' })}</span>}
								{phase === 'active' && <span className="inline-flex items-center gap-1"><Icon name={session.mode === 'screen' ? 'monitor' : 'phone'} size={12} />{durText}</span>}
							</div>
						</div>
					</div>
					{phase === 'active' && (peerMuted || peerNoMic || sharing || remoteScreenOn || anyDiagWarn) && (
						<div className="mt-2 space-y-0.5 text-ui-xs text-white/60">
							{peerMuted && <div><Icon name="volume-x" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.peerMuted', { defaultValue: '상대방이 음소거 중입니다' })}</div>}
							{peerNoMic && <div><Icon name="mic" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.peerNoMic', { defaultValue: '상대방 마이크가 감지되지 않았습니다 (소리가 오지 않음)' })}</div>}
							{sharing && (
								<div><Icon name="monitor" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.sharingLocal', { defaultValue: '내 화면을 공유하고 있습니다' })}
									{capMode && <span className="ml-1 text-white/40">· {capMode === 'native' ? t('call.capNative', { defaultValue: '네이티브 캡처' }) : t('call.capBrowser', { defaultValue: '브라우저 캡처' })}</span>}
									<span className="ml-1 text-white/40">· v{__APP_VERSION__}</span>
								</div>
							)}
							{/* 📊 미디어 흐름 진단 — 멈춘 방향만 노출 (2026-08-07 #2) */}
							{warnAudioOut && <div className="text-amber-300"><Icon name="alert-triangle" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.diagNoAudioOut', { defaultValue: '내 음성이 전송되지 않고 있습니다 (마이크 확인)' })}</div>}
							{warnAudioIn && <div className="text-amber-300"><Icon name="alert-triangle" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.diagNoAudioIn', { defaultValue: '상대방 음성이 수신되지 않고 있습니다' })}</div>}
							{warnVideoOut && (
								<div className="text-amber-300"><Icon name="alert-triangle" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.diagNoVideoOut', { defaultValue: "화면 데이터가 전송되지 않고 있습니다 — 창 대신 '전체 화면'으로 다시 공유해 보세요" })}
									{shareDbg && <span className="ml-1 text-white/40">({shareDbg})</span>}
								</div>
							)}
							{warnVideoIn && <div className="text-amber-300"><Icon name="alert-triangle" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.diagNoVideoIn', { defaultValue: '상대방 화면 데이터가 수신되지 않고 있습니다' })}</div>}
							{remoteScreenOn && remoteVideo && (screenMin || pipWin) && (
								<div className="flex flex-wrap items-center gap-x-3">
									{pipWin ? (
										<span className="text-emerald-300"><Icon name="maximize" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.screenInWindow', { defaultValue: '상대방 화면을 별도 창에서 보는 중' })}</span>
									) : (
										<>
											<button type="button" onClick={() => setScreenMin(false)} className="text-emerald-300 hover:underline">
												<Icon name="monitor" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.expandScreen', { defaultValue: '상대방 화면 크게 보기' })}
											</button>
											{pipSupported() && (
												<button type="button" onClick={() => void openScreenWindow()} className="text-emerald-300 hover:underline">
													<Icon name="maximize" size={12} className="mr-1 inline-block align-[-2px]" />{t('call.screenPopout', { defaultValue: '별도 창으로' })}
												</button>
											)}
										</>
									)}
								</div>
							)}
						</div>
					)}
					{/* 🖥 공유자 본인 미리보기 — 여기가 검으면 캡처(WebView2) 문제, 정상인데 상대가 검으면 전송 문제 (2026-08-07 #1) */}
					{phase === 'active' && sharing && (
						<div className="mt-2">
							<div className="mb-1 text-ui-xs text-white/50">{t('call.selfPreview', { defaultValue: '내 화면 미리보기 (전송 원본)' })}</div>
							<video autoPlay playsInline muted data-testid="call-self-preview"
								ref={(el) => { const tr = screenTrackRef.current; if (el && tr && !el.srcObject) el.srcObject = new MediaStream([tr]); }}
								className="max-h-28 w-full rounded-lg bg-black/50" />
						</div>
					)}
					<div className="mt-3 flex items-center justify-center gap-2.5">
						{/* 🖥 화면만 공유 세션 — 음성이 없으므로 음소거/공유 토글 없이 [공유 종료]만 */}
						{phase === 'active' && session.mode === 'voice' && (
							<>
								<button type="button" onClick={toggleMute} data-testid="call-mute" disabled={noMic}
									title={noMic ? t('call.noMicProceed', { defaultValue: '마이크가 없어 내 음성 없이 연결합니다. 상대방 소리는 들립니다.' }) : muted ? t('call.unmute', { defaultValue: '음소거 해제' }) : t('call.mute', { defaultValue: '음소거' })}
									className={`flex h-11 w-11 items-center justify-center rounded-full ${noMic ? 'bg-white/10 opacity-40' : muted ? 'bg-amber-500 text-white' : 'bg-white/15 hover:bg-white/25'}`}>
									<Icon name={muted || noMic ? 'mic-off' : 'mic'} size={19} />
								</button>
								{/* 🖥 통화 도크의 화면 공유 버튼 제거 (2026-08-14 사용자) — 공유 시작/중지는 + 시트 "화면 공유"
								    (tpt:call-share 토글)로만. startShare/stopShare 로직은 그 경로가 계속 쓰므로 유지. */}
								{/* 🔊 스피커폰 ↔ 수화기 — APK(브릿지 존재)에서만 (code 31, 2026-08-07 사용자) */}
								{typeof window !== 'undefined' && (window.TPTAndroid?.setSpeaker || window.TPTAndroid?.nativeCallSetSpeaker) && (
									<button type="button" onClick={toggleSpeaker} data-testid="call-speaker"
										title={speakerOn ? t('call.speakerToEar', { defaultValue: '수화기로 듣기' }) : t('call.speakerToLoud', { defaultValue: '스피커폰으로 듣기' })}
										className={`flex h-11 w-11 items-center justify-center rounded-full ${speakerOn ? 'bg-class-blue text-white' : 'bg-white/15 hover:bg-white/25'}`}>
										<Icon name={speakerOn ? 'volume-2' : 'volume-1'} size={19} />
									</button>
								)}
							</>
						)}
						<button type="button" onClick={() => endCall('local')} data-testid="call-hangup"
							title={session.mode === 'screen'
								? (phase === 'active' ? t('call.shareStop', { defaultValue: '공유 중지' }) : t('call.cancel', { defaultValue: '통화 취소' }))
								: (phase === 'active' ? t('call.hangup', { defaultValue: '통화 종료' }) : t('call.cancel', { defaultValue: '통화 취소' }))}
							className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600">
							<Icon name="phone-off" size={19} />
						</button>
					</div>
				</div>
			)}

			{/* 🪟 상대 화면을 별도 창(Document PiP)으로 — 대화창을 가리지 않고 본다 (2026-08-18 사용자).
			    통화 세션은 이 창에 있으므로 스트림을 옮길 수 있는 유일한 수단이 Document PiP 다. */}
			{pipWin && remoteVideo && createPortal(
				<div className="pip-root">
					<div className="pip-bar">
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="monitor" size={12} />{t('call.screenOf', { defaultValue: '{{name}}님의 화면', name: peerLabel })}</span>
					</div>
					{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
					<video autoPlay playsInline muted className="pip-video"
						ref={(el) => { if (el && el.srcObject !== remoteVideo) el.srcObject = remoteVideo; }} />
				</div>,
				pipWin.document.body,
			)}

			{/* 상대 화면 공유 수신 뷰 — 전체 오버레이(최소화 가능). 별도 창으로 뺀 동안은 감춘다. */}
			{phase === 'active' && remoteScreenOn && remoteVideo && !screenMin && !pipWin && (
				<div className="fixed inset-0 z-[1230] flex flex-col bg-black/90" data-testid="call-screen-view">
					<header className="flex items-center gap-2 px-4 py-2 text-white">
						<span className="text-ui-sm font-semibold inline-flex items-center gap-1"><Icon name="monitor" size={14} />{t('call.screenOf', { defaultValue: '{{name}}님의 화면', name: peerLabel })}</span>
						<div className="ml-auto flex items-center gap-2">
						{pipSupported() && (
							<button type="button" onClick={() => void openScreenWindow()} data-testid="call-screen-popout"
								className="rounded-md bg-white/15 px-3 py-1 text-ui-xs font-semibold hover:bg-white/25">
								{t('call.screenPopout', { defaultValue: '별도 창으로' })}
							</button>
						)}
						<button type="button" onClick={() => setScreenMin(true)}
							className="rounded-md bg-white/15 px-3 py-1 text-ui-xs font-semibold hover:bg-white/25">
							{t('call.minimize', { defaultValue: '최소화' })}
						</button>
						{/* ✕ = 최소화와 같다(통화는 끊지 않는다) — 2026-10-08 형 "닫기는 X 로" */}
						<button type="button" onClick={() => setScreenMin(true)} aria-label={t('call.minimize', { defaultValue: '최소화' }) as string} data-testid="call-screen-close"
							className="rounded-md bg-white/15 px-2 py-1 text-ui-xs font-semibold hover:bg-white/25">✕</button>
						</div>
					</header>
					<div className="flex min-h-0 flex-1 items-center justify-center p-2">
						<video autoPlay playsInline muted
							ref={(el) => { if (el && el.srcObject !== remoteVideo) el.srcObject = remoteVideo; }}
							className="max-h-full max-w-full rounded-lg" />
					</div>
				</div>
			)}
		</>
	);
}
